import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { 
  Settings, Calendar, Clock, Plus, Trash2, Save, Loader2, 
  AlertTriangle, CheckCircle, X, Shield, Users, ChevronDown, ChevronUp, Eye, Search,
  RefreshCw, Zap, Edit3, Share2, Star, Gift, Archive, Globe, Lock, ShoppingCart, Mail, Trophy
} from "lucide-react";
import { Link } from "react-router-dom";
import MatchDayCompositionRules from "../components/admin/MatchDayCompositionRules";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const { user, dbUser, getAuthHeader, getAsyncAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Admin status from MongoDB
  const [isAdmin, setIsAdmin] = useState(false);
  const [tbcMatches, setTbcMatches] = useState([]);
  
  // Transfer windows
  const [windows, setWindows] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Admin management
  const [admins, setAdmins] = useState([]);
  const [predictorAdmins, setPredictorAdmins] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newPredictorAdminEmail, setNewPredictorAdminEmail] = useState("");
  
  // Stats
  const [stats, setStats] = useState({});
  
  // User management
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUsersSection, setShowUsersSection] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserTeam, setSelectedUserTeam] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  const toggleUserSelect = (userId) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.size === filteredUsers.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(filteredUsers.map(u => u.id)));
    }
  };
  
  // Scorecard Sync (NEW)
  const [syncMatchId, setSyncMatchId] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showScorecardSection, setShowScorecardSection] = useState(false);
  
  // Points Adjustment (NEW)
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustUserId, setAdjustUserId] = useState("");
  const [adjustPoints, setAdjustPoints] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  
  // All leagues (admin view)
  const [allLeagues, setAllLeagues] = useState([]);
  const [showLeaguesSection, setShowLeaguesSection] = useState(false);
  
  // Match assignment for transfer windows
  const [completedMatches, setCompletedMatches] = useState([]);
  const [editingWindowMatches, setEditingWindowMatches] = useState(null); // window id being edited
  const [windowMatchIds, setWindowMatchIds] = useState([]);
  
  // Purple & Orange Cap
  const [capWeekNumber, setCapWeekNumber] = useState(1);
  const [capResult, setCapResult] = useState(null);
  
  // Add Player
  const [showAddPlayerSection, setShowAddPlayerSection] = useState(false);
  const [newPlayerCricbuzzId, setNewPlayerCricbuzzId] = useState("");
  const [newPlayerName, setNewPlayerName] = useState("");
  const [newPlayerCountry, setNewPlayerCountry] = useState("");
  const [newPlayerRole, setNewPlayerRole] = useState("Batsman");
  const [newPlayerCredits, setNewPlayerCredits] = useState("8.0");
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [addPlayerResult, setAddPlayerResult] = useState(null);
  const [fetchingCricbuzz, setFetchingCricbuzz] = useState(false);

  // Admin Team Editor
  const [editTeamUserId, setEditTeamUserId] = useState("");
  const [editTeamData, setEditTeamData] = useState(null);
  const [editTeamPlayers, setEditTeamPlayers] = useState([]);
  const [editTeamAllPlayers, setEditTeamAllPlayers] = useState([]);
  const [editTeamSearch, setEditTeamSearch] = useState("");
  const [editTeamCaptain, setEditTeamCaptain] = useState("");
  const [editTeamVC, setEditTeamVC] = useState("");
  const [editTeamName, setEditTeamName] = useState("");
  const [editTeamSaving, setEditTeamSaving] = useState(false);
  const [editTeamLoading, setEditTeamLoading] = useState(false);
  const [recalcMatchId, setRecalcMatchId] = useState("");
  const [recalcPreview, setRecalcPreview] = useState(null);
  const [recalcOldPoints, setRecalcOldPoints] = useState("");
  const [fixWeekKey, setFixWeekKey] = useState("");
  const [fixWeekPts, setFixWeekPts] = useState("");
  
  // Super Over
  const [showSuperOverSection, setShowSuperOverSection] = useState(false);
  const [soMatchId, setSoMatchId] = useState("");
  const [soNumber, setSoNumber] = useState(1);
  const [soMatchPlayers, setSoMatchPlayers] = useState([]);
  const [soLoadingPlayers, setSoLoadingPlayers] = useState(false);
  const [soBatsmen, setSoBatsmen] = useState([{ player_id: "", runs: 0, balls: 0, fours: 0, sixes: 0, is_out: false }]);
  const [soBowlers, setSoBowlers] = useState([{ player_id: "", overs: 1, runs: 0, wickets: 0, maidens: 0 }]);
  const [soFielding, setSoFielding] = useState([]);
  const [soExistingData, setSoExistingData] = useState(null);
  const [soSubmitting, setSoSubmitting] = useState(false);

  // Match Sponsors
  const [sponsoredMatches, setSponsoredMatches] = useState([]);

  // Match Day Manual Open control
  const [showMatchDaySection, setShowMatchDaySection] = useState(false);
  const [mdMatches, setMdMatches] = useState([]);
  const [mdLoading, setMdLoading] = useState(false);
  const [mdMinutes, setMdMinutes] = useState(30);
  const [mdBusyId, setMdBusyId] = useState(null);

  const fetchMdMatches = async () => {
    setMdLoading(true);
    try {
      const res = await axios.get(`${API}/matches`, { headers: getAuthHeader() });
      const list = (res.data.matches || res.data || [])
        .filter(m => ["upcoming", "live"].includes(m.status) && m.team1 !== "TBC" && m.team2 !== "TBC")
        .sort((a, b) => (a.match_number || 0) - (b.match_number || 0));
      setMdMatches(list);
    } catch (e) {
      setError("Failed to load matches");
    }
    setMdLoading(false);
  };

  const mdOverrideActive = (m) => {
    const raw = m.daily_lock_override_until;
    if (!raw) return null;
    const t = new Date(raw);
    return t > new Date() ? t : null;
  };

  const setMatchDayOpen = async (matchId, minutes) => {
    setMdBusyId(matchId);
    try {
      const res = await axios.post(`${API}/admin/matches/${matchId}/open-daily`, { minutes }, { headers: getAuthHeader() });
      setSuccess(res.data.message || "Updated");
      setError("");
      await fetchMdMatches();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update Match Day window");
    }
    setMdBusyId(null);
  };


  // App Config Toggles
  const [appConfig, setAppConfig] = useState({ winners_announced: false, coming_soon: false });
  const [togglingConfig, setTogglingConfig] = useState(false);

  // Feedback Dashboard
  const [showFeedbackSection, setShowFeedbackSection] = useState(false);
  const [feedbackData, setFeedbackData] = useState({ feedbacks: [], analytics: {}, total: 0 });
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  // Tier Management
  const [showTierSection, setShowTierSection] = useState(false);
  const [tierUsers, setTierUsers] = useState([]);
  const [tierSearch, setTierSearch] = useState("");
  const [tierLoading, setTierLoading] = useState(false);

  // Admin Name Editor
  const [showNameEditModal, setShowNameEditModal] = useState(false);
  const [nameEditUser, setNameEditUser] = useState(null);
  const [nameEditDisplayName, setNameEditDisplayName] = useState("");
  const [nameEditTeamName, setNameEditTeamName] = useState("");
  const [nameEditSaving, setNameEditSaving] = useState(false);

  // Purchase Management Modal
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseModalUser, setPurchaseModalUser] = useState(null);
  const [purchaseData, setPurchaseData] = useState(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [grantSizeLeague, setGrantSizeLeague] = useState("");
  const [grantSizeValue, setGrantSizeValue] = useState("15");
  const [grantSlotType, setGrantSlotType] = useState("private");
  const [granting, setGranting] = useState(false);

  // Budget Bonus
  const [grantBonusAmount, setGrantBonusAmount] = useState("1");
  const [grantingBonus, setGrantingBonus] = useState(false);
  const [bonusGrantSuccess, setBonusGrantSuccess] = useState("");

  // Sponsor Management
  const [showSponsorSection, setShowSponsorSection] = useState(false);
  const [sponsorMatchId, setSponsorMatchId] = useState("");
  const [sponsorName, setSponsorName] = useState("");
  const [sponsorLogoUrl, setSponsorLogoUrl] = useState("");
  const [sponsorLink, setSponsorLink] = useState("");
  const [sponsorDescription, setSponsorDescription] = useState("");
  
  // League Admin
  const [showLeagueAdminSection, setShowLeagueAdminSection] = useState(false);
  const [leagueAdminSearchEmail, setLeagueAdminSearchEmail] = useState("");
  const [leagueAdminLoading, setLeagueAdminLoading] = useState(false);
  const [leagueAdminResults, setLeagueAdminResults] = useState(null);

  // Push Broadcast
  const [showPushSection, setShowPushSection] = useState(false);
  const [pushTitle, setPushTitle] = useState("");
  const [pushBody, setPushBody] = useState("");
  const [pushTarget, setPushTarget] = useState("all");
  const [pushDeepLink, setPushDeepLink] = useState("");
  const [pushSending, setPushSending] = useState(false);
  const [lastBroadcastResult, setLastBroadcastResult] = useState(null);
  const [backfillingRanks, setBackfillingRanks] = useState(false);

  // Tournament Management
  const [showTournamentSection, setShowTournamentSection] = useState(false);
  const [activeTournament, setActiveTournament] = useState(null);
  const [allTournaments, setAllTournaments] = useState([]);
  const [newTournamentName, setNewTournamentName] = useState("");
  const [newTournamentSeriesId, setNewTournamentSeriesId] = useState("");
  const [initializingTournament, setInitializingTournament] = useState(false);
  const [syncingMatches, setSyncingMatches] = useState(false);
  const [syncingPlayers, setSyncingPlayers] = useState(false);
  
  // Late Joiner Backfill
  const [showBackfillSection, setShowBackfillSection] = useState(false);
  const [pendingBackfillUsers, setPendingBackfillUsers] = useState([]);
  const [backfillStats, setBackfillStats] = useState({ pending_count: 0, backfilled_count: 0 });
  const [selectedBackfillUsers, setSelectedBackfillUsers] = useState([]);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillProcessing, setBackfillProcessing] = useState(false);
  const [backfillResults, setBackfillResults] = useState(null);
  
  // New window form
  const [newWindow, setNewWindow] = useState({
    name: "",
    week_number: 1,
    start_date: "",
    end_date: "",
    description: "",
    new_users_only: false,
    finals_mode: false
  });

  // Captain/VC unlimited override
  const [captainUnlimitedUntil, setCaptainUnlimitedUntil] = useState("");
  const [captainUnlimitedActive, setCaptainUnlimitedActive] = useState(false);
  const [captainUnlimitedInput, setCaptainUnlimitedInput] = useState("");
  const [captainUnlimitedSaving, setCaptainUnlimitedSaving] = useState(false);

  const fetchData = async () => {
    try {
      // Use async auth header to ensure fresh token is available
      const headers = await getAsyncAuthHeader();
      // Check admin status from MongoDB
      const adminCheck = await axios.get(`${API}/auth/admin-status`, { headers });
      setIsAdmin(adminCheck.data.is_admin);
      
      if (adminCheck.data.is_admin) {
        const [statsRes, windowsRes, adminsRes, predictorAdminsRes, tourRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { headers }),
          axios.get(`${API}/transfer-windows`).catch(() => ({ data: { windows: [] } })),
          axios.get(`${API}/admin/list-admins`, { headers: getAuthHeader() }).catch(() => ({ data: { admins: [] } })),
          axios.get(`${API}/admin/list-predictor-admins`, { headers: getAuthHeader() }).catch(() => ({ data: { predictor_admins: [] } })),
          axios.get(`${API}/admin/tournament/active`, { headers: getAuthHeader() }).catch(() => ({ data: { active_tournament: null, all_tournaments: [] } }))
        ]);
        
        setStats(statsRes.data);
        const loadedWindows = windowsRes.data.windows || [];
        setWindows(loadedWindows);
        setAdmins(adminsRes.data.admins || []);
        setPredictorAdmins(predictorAdminsRes.data.predictor_admins || []);
        
        // Set tournament info first (needed for week calculation)
        const fetchedActiveTournament = tourRes.data.active_tournament;
        setActiveTournament(fetchedActiveTournament);
        setAllTournaments(tourRes.data.all_tournaments || []);
        
        // Fetch TBC matches
        try {
          const tbcRes = await axios.get(`${API}/admin/tbc-matches`, { headers: getAuthHeader() });
          setTbcMatches(tbcRes.data.matches || []);
        } catch { setTbcMatches([]); }
        
        // Auto-set next week number for new window form - ONLY count windows for active tournament
        const activeTournamentId = fetchedActiveTournament?.tournament_id || "";
        const tournamentWindows = loadedWindows.filter(w => w.tournament_id === activeTournamentId);
        const maxWeek = tournamentWindows.reduce((max, w) => Math.max(max, w.week_number || 0), 0);
        setNewWindow(prev => ({ ...prev, week_number: maxWeek + 1, name: `Week ${maxWeek + 1}` }));
        setCapWeekNumber(maxWeek || 1);

        // Fetch app config
        try {
          const configRes = await axios.get(`${API}/app-config`);
          setAppConfig(configRes.data || { winners_announced: false, coming_soon: false });
        } catch { /* ignore */ }

        // Fetch captain/VC unlimited override
        try {
          const capRes = await axios.get(`${API}/admin/captain-change/unlimited-until`, { headers: getAuthHeader() });
          const v = capRes.data.captain_change_unlimited_until || "";
          setCaptainUnlimitedUntil(v);
          setCaptainUnlimitedActive(!!capRes.data.active);
          // Convert ISO -> datetime-local input value (strip seconds + Z)
          if (v) {
            try {
              const d = new Date(v);
              const pad = (n) => String(n).padStart(2, "0");
              const local = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
              setCaptainUnlimitedInput(local);
            } catch { setCaptainUnlimitedInput(""); }
          } else {
            setCaptainUnlimitedInput("");
          }
        } catch { /* ignore */ }
      }
    } catch (e) {
      console.error("Failed to fetch data:", e);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchData();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggleAppConfig = async (key) => {
    setTogglingConfig(true);
    try {
      const newVal = !appConfig[key];
      await axios.post(`${API}/admin/app-config?${key}=${newVal}`, {}, { headers: getAuthHeader() });
      setAppConfig(prev => ({ ...prev, [key]: newVal }));
      setSuccess(`${key.replace('_', ' ')} ${newVal ? 'enabled' : 'disabled'}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update config");
    } finally {
      setTogglingConfig(false);
    }
  };

  const fetchAllLeagues = async () => {
    try {
      const res = await axios.get(`${API}/admin/all-leagues`, { headers: getAuthHeader() });
      setAllLeagues(res.data.leagues || []);
    } catch (e) {
      console.error("Failed to fetch leagues:", e);
    }
  };

  const fetchFeedbackData = async () => {
    setFeedbackLoading(true);
    try {
      const res = await axios.get(`${API}/admin/feedback`, { headers: getAuthHeader() });
      setFeedbackData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load feedback");
    } finally {
      setFeedbackLoading(false);
    }
  };

  const fetchPendingBackfillUsers = async () => {
    setBackfillLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pending-backfill-users`, { headers: getAuthHeader() });
      setPendingBackfillUsers(res.data.pending_users || []);
      setBackfillStats({
        pending_count: res.data.pending_count || 0,
        backfilled_count: res.data.backfilled_count || 0,
        total_teams: res.data.total_teams || 0,
        _debug: res.data._debug || null,
        message: res.data.message || null
      });
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load pending backfill users");
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleBackfillAll = async () => {
    if (!window.confirm("This will backfill retroactive points and apply late joiner penalties for ALL pending users. Continue?")) return;
    setBackfillProcessing(true);
    setBackfillResults(null);
    try {
      const res = await axios.post(`${API}/admin/backfill-late-joiners`, {}, { headers: getAuthHeader() });
      setBackfillResults(res.data);
      setSuccess(`Backfilled ${res.data.users_processed} users`);
      fetchPendingBackfillUsers();
    } catch (e) {
      setError(e.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfillProcessing(false);
    }
  };

  const handleBackfillSelected = async () => {
    if (selectedBackfillUsers.length === 0) {
      setError("Select at least one user to backfill");
      return;
    }
    if (!window.confirm(`Backfill ${selectedBackfillUsers.length} selected users?`)) return;
    setBackfillProcessing(true);
    setBackfillResults(null);
    try {
      const res = await axios.post(`${API}/admin/backfill-late-joiners`, 
        { user_ids: selectedBackfillUsers },
        { headers: getAuthHeader() }
      );
      setBackfillResults(res.data);
      setSuccess(`Backfilled ${res.data.users_processed} users`);
      setSelectedBackfillUsers([]);
      fetchPendingBackfillUsers();
    } catch (e) {
      setError(e.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfillProcessing(false);
    }
  };

  const handleResetBackfill = async (userId) => {
    if (!window.confirm("This will reset backfill for this user, reversing their retroactive points and penalty. They will need to be backfilled again. Continue?")) return;
    try {
      const res = await axios.post(`${API}/admin/reset-backfill/${userId}`, {}, { headers: getAuthHeader() });
      setSuccess(`Reset backfill for user. Reversed ${res.data.retroactive_points_reversed} points and ${res.data.penalty_reversed} penalty.`);
      fetchPendingBackfillUsers();
    } catch (e) {
      setError(e.response?.data?.detail || "Reset failed");
    }
  };

  const toggleBackfillUserSelection = (userId) => {
    setSelectedBackfillUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const fetchCompletedMatches = async () => {
    try {
      const res = await axios.get(`${API}/matches`);
      // Sort matches by match_number for proper ordering (1, 2, 3... not 1, 10, 11...)
      const sortedMatches = (res.data.matches || []).sort((a, b) => 
        (a.match_number || 0) - (b.match_number || 0)
      );
      setCompletedMatches(sortedMatches);
    } catch (e) {
      console.error("Failed to fetch matches:", e);
    }
  };

  const saveWindowMatches = async (windowId) => {
    try {
      await axios.put(
        `${API}/admin/transfer-windows/${windowId}/matches`,
        { match_ids: windowMatchIds },
        { headers: getAuthHeader() }
      );
      setSuccess("Matches assigned to window successfully");
      setEditingWindowMatches(null);
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to assign matches");
    }
  };

  const createWindow = async (e) => {
    e.preventDefault();
    if (!newWindow.name || !newWindow.start_date || !newWindow.end_date) {
      setError("Please fill all required fields");
      return;
    }

    setSaving(true);
    setError("");

    try {
      await axios.post(`${API}/admin/transfer-windows`, newWindow, { headers: getAuthHeader() });
      setSuccess("Transfer window created!");
      setShowCreateModal(false);
      const nextWeek = (windows.length > 0 ? Math.max(...windows.map(w => w.week_number || 0)) : 0) + 2;
      setNewWindow({ name: `Week ${nextWeek}`, week_number: nextWeek, start_date: "", end_date: "", description: "", new_users_only: false, finals_mode: false });
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create window");
    } finally {
      setSaving(false);
    }
  };

  const toggleWindow = async (windowId, activate) => {
    try {
      const token = await user.getIdToken(true);
      const endpoint = activate 
        ? `${API}/admin/transfer-windows/${windowId}/activate`
        : `${API}/admin/transfer-windows/${windowId}/deactivate`;
      await axios.put(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(activate ? "Window activated!" : "Window deactivated!");
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update window");
    }
  };

  const deleteWindow = async (windowId) => {
    if (!window.confirm("Delete this transfer window?")) return;

    try {
      // Get a fresh token to ensure authentication works
      const token = await user.getIdToken(true);
      await axios.delete(`${API}/admin/transfer-windows/${windowId}`, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Window deleted!");
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to delete window");
    }
  };

  const grantAdmin = async (e) => {
    e.preventDefault();
    if (!newAdminEmail.trim()) return;

    try {
      await axios.post(`${API}/admin/grant-admin/${newAdminEmail.trim()}`, {}, { headers: getAuthHeader() });
      setSuccess(`Admin access granted to ${newAdminEmail}`);
      setNewAdminEmail("");
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to grant admin access");
    }
  };

  const revokeAdmin = async (email) => {
    if (!window.confirm(`Revoke admin access from ${email}?`)) return;

    try {
      await axios.post(`${API}/admin/revoke-admin/${email}`, {}, { headers: getAuthHeader() });
      setSuccess(`Admin access revoked from ${email}`);
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to revoke admin access");
    }
  };

  // Predictor Admin management
  const grantPredictorAdmin = async (e) => {
    e.preventDefault();
    if (!newPredictorAdminEmail.trim()) return;

    try {
      await axios.post(`${API}/admin/set-predictor-admin/${newPredictorAdminEmail.trim()}`, {}, { headers: getAuthHeader() });
      setSuccess(`Predictor admin access granted to ${newPredictorAdminEmail}`);
      setNewPredictorAdminEmail("");
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to grant predictor admin access");
    }
  };

  const revokePredictorAdmin = async (email) => {
    if (!window.confirm(`Revoke predictor admin access from ${email}?`)) return;

    try {
      await axios.post(`${API}/admin/revoke-predictor-admin/${email}`, {}, { headers: getAuthHeader() });
      setSuccess(`Predictor admin access revoked from ${email}`);
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to revoke predictor admin access");
    }
  };

  // Fetch all users (NEW)
  const fetchAllUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: getAuthHeader() });
      setAllUsers(res.data.users || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to fetch users");
    } finally {
      setUsersLoading(false);
    }
  };

  // View user team details (NEW)
  const viewUserTeam = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}/team`, { headers: getAuthHeader() });
      setSelectedUserTeam(res.data);
    } catch (e) {
      setError("Failed to fetch team details");
    }
  };

  const loadTeamForEdit = async (userId) => {
    setEditTeamLoading(true);
    setEditTeamUserId(userId);
    try {
      const [teamRes, playersRes] = await Promise.all([
        axios.get(`${API}/admin/users/${userId}/team`, { headers: getAuthHeader() }),
        axios.get(`${API}/players`, { headers: getAuthHeader() }),
      ]);
      const team = teamRes.data.team;
      const teamPlayers = teamRes.data.players || [];
      setEditTeamData(team);
      setEditTeamPlayers(teamPlayers);
      setEditTeamCaptain(team?.captain_id || "");
      setEditTeamVC(team?.vice_captain_id || "");
      setEditTeamName(team?.name || "");
      // Filter to only IPL players (ipl_ prefix) — exclude old T20 WC UUID players
      const allPlayers = (playersRes.data.players || playersRes.data || []).filter(p => p.id && p.id.startsWith("ipl_"));
      setEditTeamAllPlayers(allPlayers);
      // Show note if user has no team for current tournament
      if (!team && teamRes.data.note) {
        setError(teamRes.data.note);
      }
    } catch (e) {
      setError("Failed to load team for editing");
    } finally {
      setEditTeamLoading(false);
    }
  };

  const removeFromEditTeam = (playerId) => {
    setEditTeamPlayers(prev => prev.filter(p => p.id !== playerId));
    if (editTeamCaptain === playerId) setEditTeamCaptain("");
    if (editTeamVC === playerId) setEditTeamVC("");
  };

  const addToEditTeam = (player) => {
    if (editTeamPlayers.length >= 11) return;
    if (editTeamPlayers.find(p => p.id === player.id)) return;
    setEditTeamPlayers(prev => [...prev, player]);
    setEditTeamSearch("");
  };

  const saveEditTeam = async () => {
    if (editTeamPlayers.length !== 11) { setError("Team must have exactly 11 players"); return; }
    if (!editTeamCaptain) { setError("Select a captain"); return; }
    if (!editTeamVC) { setError("Select a vice-captain"); return; }
    if (editTeamCaptain === editTeamVC) { setError("Captain and VC must be different"); return; }
    setEditTeamSaving(true);
    try {
      await axios.put(`${API}/admin/users/${editTeamUserId}/team`, {
        name: editTeamName,
        players: editTeamPlayers.map(p => p.id),
        captain_id: editTeamCaptain,
        vice_captain_id: editTeamVC,
      }, { headers: getAuthHeader() });
      setSuccess("Team updated successfully!");
      setEditTeamUserId("");
      setEditTeamData(null);
      setEditTeamPlayers([]);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update team");
    } finally {
      setEditTeamSaving(false);
    }
  };

  // Toggle users section (NEW)
  const toggleUsersSection = () => {
    if (!showUsersSection && allUsers.length === 0) {
      fetchAllUsers();
    }
    setShowUsersSection(!showUsersSection);
  };

  // Filter users by search (NEW)
  const filteredUsers = allUsers.filter(u => {
    const q = userSearchQuery.toLowerCase();
    return !q || 
      (u.email || "").toLowerCase().includes(q) ||
      (u.display_name || "").toLowerCase().includes(q) ||
      (u.team_name || "").toLowerCase().includes(q) ||
      (u.referral_code || "").toLowerCase().includes(q);
  });

  // Sync scorecard for a match
  const syncScorecard = async (force = false) => {
    if (!syncMatchId.trim()) {
      setError("Please enter a match ID");
      return;
    }
    
    setSyncing(true);
    setError("");
    setSyncResult(null);
    
    try {
      const url = force 
        ? `${API}/admin/sync-scorecard/${syncMatchId.trim()}?force=true`
        : `${API}/admin/sync-scorecard/${syncMatchId.trim()}`;
      const res = await axios.post(url, {}, { headers: getAuthHeader() });
      setSyncResult(res.data);
      setSuccess(`Scorecard synced! ${res.data.performances_added} performances, ${res.data.teams_updated} teams updated.`);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to sync scorecard");
    } finally {
      setSyncing(false);
    }
  };

  // Check sync status
  const checkSyncStatus = async () => {
    if (!syncMatchId.trim()) {
      setError("Please enter a match ID");
      return;
    }
    
    try {
      const res = await axios.get(`${API}/admin/sync-status/${syncMatchId.trim()}`, { headers: getAuthHeader() });
      setSyncResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to check status");
    }
  };

  // Backfill weekly points from match_points
  const backfillWeeklyPoints = async () => {
    setSyncing(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/backfill-weekly-points`, {}, { headers: getAuthHeader() });
      setSuccess(`Weekly points backfilled! ${res.data.users_updated} users, ${res.data.teams_updated} teams updated.`);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to backfill weekly points");
    } finally {
      setSyncing(false);
    }
  };

  // Force recalculate daily points for a match
  const forceRecalcDailyPoints = async () => {
    if (!syncMatchId.trim()) return;
    setSyncing(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/calculate-daily-points/${syncMatchId.trim()}?force=true`, {}, { headers: getAuthHeader() });
      setSuccess(`Daily points recalculated for match ${syncMatchId}! ${res.data.teams_scored} teams scored, ${res.data.reversed_count || 0} reversed.`);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to recalculate daily points");
    } finally {
      setSyncing(false);
    }
  };

  // Calculate Purple & Orange Caps
  const calculateCaps = async () => {
    setSyncing(true);
    setError("");
    setCapResult(null);
    try {
      const res = await axios.post(`${API}/admin/calculate-caps/${capWeekNumber}`, {}, { headers: getAuthHeader() });
      setCapResult(res.data);
      setSuccess(`Caps calculated for Week ${capWeekNumber}! Orange: ${res.data.orange_cap?.winners?.length || 0} winners, Purple: ${res.data.purple_cap?.winners?.length || 0} winners`);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to calculate caps");
    } finally {
      setSyncing(false);
    }
  };

  // Super Over: Load match players
  const loadSoMatchPlayers = async () => {
    if (!soMatchId) return;
    setSoLoadingPlayers(true);
    try {
      const res = await axios.get(`${API}/matches/${soMatchId}/players`);
      setSoMatchPlayers(res.data.players || []);
      // Also load existing super over data
      const soRes = await axios.get(`${API}/admin/super-overs/${soMatchId}`, { headers: getAuthHeader() });
      setSoExistingData(soRes.data.super_overs || {});
      setSuccess(`Loaded ${res.data.players?.length || 0} players from match ${soMatchId}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load match players. Make sure the match has been synced first.");
      setSoMatchPlayers([]);
    } finally {
      setSoLoadingPlayers(false);
    }
  };

  // Super Over: Submit
  const submitSuperOver = async () => {
    setSoSubmitting(true);
    setError("");
    try {
      const validBatsmen = soBatsmen.filter(b => b.player_id);
      const validBowlers = soBowlers.filter(b => b.player_id);
      const validFielding = soFielding.filter(f => f.player_id);
      
      if (validBatsmen.length === 0 && validBowlers.length === 0) {
        setError("Add at least one batsman or bowler");
        setSoSubmitting(false);
        return;
      }
      
      const res = await axios.post(`${API}/admin/super-over`, {
        match_id: soMatchId,
        super_over_number: soNumber,
        batting_team: "",
        bowling_team: "",
        batsmen: validBatsmen,
        bowlers: validBowlers,
        fielding: validFielding
      }, { headers: getAuthHeader() });
      
      setSuccess(`Super Over ${soNumber} added! ${res.data.performances_added} performances, ${res.data.total_fantasy_points_distributed} total pts distributed`);
      // Reload existing data
      const soRes = await axios.get(`${API}/admin/super-overs/${soMatchId}`, { headers: getAuthHeader() });
      setSoExistingData(soRes.data.super_overs || {});
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to add super over data");
    } finally {
      setSoSubmitting(false);
    }
  };

  const updateSoBatsman = (index, field, value) => {
    const updated = [...soBatsmen];
    updated[index] = { ...updated[index], [field]: value };
    setSoBatsmen(updated);
  };

  const updateSoBowler = (index, field, value) => {
    const updated = [...soBowlers];
    updated[index] = { ...updated[index], [field]: value };
    setSoBowlers(updated);
  };

  // Adjust points
  const adjustUserPoints = async (e) => {
    e.preventDefault();
    if (!adjustUserId || !adjustPoints || !adjustReason) {
      setError("Please fill all fields");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/adjust-points`, {
        user_id: adjustUserId,
        points: parseFloat(adjustPoints),
        reason: adjustReason
      }, { headers: getAuthHeader() });
      
      setSuccess(`Points adjusted: ${adjustPoints} points`);
      setShowAdjustModal(false);
      setAdjustUserId("");
      setAdjustPoints("");
      setAdjustReason("");
      fetchAllUsers(); // Refresh user list
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to adjust points");
    }
  };

  // Save user name edit
  const saveNameEdit = async () => {
    if (!nameEditUser) return;
    
    setNameEditSaving(true);
    try {
      await axios.put(`${API}/admin/users/${nameEditUser.id}/update-name`, {
        display_name: nameEditDisplayName.trim() || undefined,
        preferred_team_name: nameEditTeamName.trim() || undefined
      }, { headers: getAuthHeader() });
      
      setSuccess("User name updated successfully");
      setShowNameEditModal(false);
      setNameEditUser(null);
      setNameEditDisplayName("");
      setNameEditTeamName("");
      fetchAllUsers(); // Refresh
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to update name");
    } finally {
      setNameEditSaving(false);
    }
  };

  // Fetch user purchases for modal
  const openPurchaseModal = async (user) => {
    setPurchaseModalUser(user);
    setShowPurchaseModal(true);
    setPurchaseLoading(true);
    setPurchaseData(null);
    setGrantSizeLeague("");
    setGrantSizeValue("15");
    setGrantSlotType("private");
    try {
      const headers = await getAsyncAuthHeader();
      const res = await axios.get(`${API}/admin/users/${user.id}/purchases`, { headers });
      setPurchaseData(res.data);
      if (res.data.leagues?.length > 0) {
        setGrantSizeLeague(res.data.leagues[0].id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load purchase data");
      setShowPurchaseModal(false);
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleGrantSize = async () => {
    if (!purchaseModalUser || !grantSizeLeague) return;
    setGranting(true);
    try {
      const headers = await getAsyncAuthHeader();
      const res = await axios.post(`${API}/admin/users/${purchaseModalUser.id}/grant-league-size`, {
        league_id: grantSizeLeague,
        size: parseInt(grantSizeValue),
      }, { headers });
      setSuccess(res.data?.message || "League size upgrade granted");
      // Refresh purchase data
      const refreshed = await axios.get(`${API}/admin/users/${purchaseModalUser.id}/purchases`, { headers });
      setPurchaseData(refreshed.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to grant size upgrade");
    } finally {
      setGranting(false);
    }
  };

  const handleGrantSlot = async () => {
    if (!purchaseModalUser) return;
    setGranting(true);
    try {
      const headers = await getAsyncAuthHeader();
      const res = await axios.post(`${API}/admin/users/${purchaseModalUser.id}/grant-league-slot`, {
        type: grantSlotType,
      }, { headers });
      setSuccess(res.data?.message || `Granted ${grantSlotType} league slot`);
      const refreshed = await axios.get(`${API}/admin/users/${purchaseModalUser.id}/purchases`, { headers });
      setPurchaseData(refreshed.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to grant slot");
    } finally {
      setGranting(false);
    }
  };

  const handleGrantBudgetBonus = async () => {
    if (!purchaseModalUser) return;
    setGrantingBonus(true);
    setBonusGrantSuccess("");
    try {
      const headers = await getAsyncAuthHeader();
      const res = await axios.put(
        `${API}/admin/users/${purchaseModalUser.id}/budget-bonus`,
        { bonus: parseInt(grantBonusAmount) },
        { headers }
      );
      setBonusGrantSuccess(res.data.message);
      setTimeout(() => setBonusGrantSuccess(""), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to grant bonus");
    } finally {
      setGrantingBonus(false);
    }
  };

  const handleClearBudgetBonus = async () => {
    if (!purchaseModalUser) return;
    setGrantingBonus(true);
    try {
      const headers = await getAsyncAuthHeader();
      const res = await axios.put(
        `${API}/admin/users/${purchaseModalUser.id}/budget-bonus`,
        { bonus: 0 },
        { headers }
      );
      setBonusGrantSuccess(res.data.message);
      setTimeout(() => setBonusGrantSuccess(""), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to clear bonus");
    } finally {
      setGrantingBonus(false);
    }
  };

  // Clear messages after 3 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess("");
        setError("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  if (!user) {
    return (
      <div className="text-center py-12" data-testid="admin-login-prompt">
        <Shield className="mx-auto text-slate-600 mb-4" size={48} />
        <p className="text-slate-400 mb-4">Please login to access admin panel</p>
        <Link to="/auth" className="text-emerald-400 hover:underline">Login</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-400" size={48} />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="text-center py-12" data-testid="admin-no-access">
        <AlertTriangle className="mx-auto text-yellow-500 mb-4" size={48} />
        <p className="text-slate-400 mb-2">Access Denied</p>
        <p className="text-slate-500 text-sm mb-4">You don't have admin privileges</p>
        <p className="text-slate-600 text-xs">Contact an existing admin to get access</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="text-emerald-400" size={28} />
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
            <p className="text-slate-400 text-sm">Manage transfer windows and admin access</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-400 rounded-lg p-3 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 rounded-lg p-3 text-sm flex items-center gap-2">
          <CheckCircle size={16} />
          {success}
        </div>
      )}

      {/* App Mode Toggles */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4" data-testid="app-config-toggles">
        <h3 className="text-white font-semibold mb-3">App Mode Controls</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className={`flex items-center justify-between p-3 rounded-lg border ${appConfig.winners_announced ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-900/50 border-slate-700'}`}>
            <div>
              <p className="text-white font-medium text-sm">Announce Winners</p>
              <p className="text-slate-400 text-xs">Shows confetti + winner popup on all pages</p>
            </div>
            <button
              onClick={() => toggleAppConfig('winners_announced')}
              disabled={togglingConfig}
              className={`relative w-12 h-6 rounded-full transition-all ${appConfig.winners_announced ? 'bg-amber-500' : 'bg-slate-600'}`}
              data-testid="toggle-winners-announced"
            >
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${appConfig.winners_announced ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
          <div className={`flex items-center justify-between p-3 rounded-lg border ${appConfig.coming_soon ? 'bg-orange-500/10 border-orange-500/30' : 'bg-slate-900/50 border-slate-700'}`}>
            <div>
              <p className="text-white font-medium text-sm">Coming Soon Mode</p>
              <p className="text-slate-400 text-xs">Replaces homepage with APL 2026 countdown</p>
            </div>
            <button
              onClick={() => toggleAppConfig('coming_soon')}
              disabled={togglingConfig}
              className={`relative w-12 h-6 rounded-full transition-all ${appConfig.coming_soon ? 'bg-orange-500' : 'bg-slate-600'}`}
              data-testid="toggle-coming-soon"
            >
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${appConfig.coming_soon ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <p className="text-2xl font-bold text-white">{stats.matches || 0}</p>
          <p className="text-slate-400 text-sm">Matches</p>
        </div>
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <p className="text-2xl font-bold text-white">{stats.players || 0}</p>
          <p className="text-slate-400 text-sm">Players</p>
        </div>
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <p className="text-2xl font-bold text-white">{stats.users || 0}</p>
          <p className="text-slate-400 text-sm">Users</p>
        </div>
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <p className="text-2xl font-bold text-white">{stats.teams || 0}</p>
          <p className="text-slate-400 text-sm">Teams</p>
        </div>
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <p className="text-2xl font-bold text-white">{stats.leagues || 0}</p>
          <p className="text-slate-400 text-sm">Leagues</p>
        </div>
      </div>

      {/* Analytics Dashboard Link */}
      <Link 
        to="/admin/analytics" 
        className="block bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl p-4 hover:bg-emerald-500/20 transition-all"
        data-testid="analytics-dashboard-link"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Zap className="text-emerald-400" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Analytics Dashboard</h3>
              <p className="text-sm text-slate-400">View detailed stats, user activity, league insights</p>
            </div>
          </div>
          <ChevronDown className="text-emerald-400 rotate-[-90deg]" size={24} />
        </div>
      </Link>

      {/* Email Queue Link */}
      <Link
        to="/admin/emails"
        className="block bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4 hover:bg-purple-500/20 transition-all"
        data-testid="email-queue-link"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Mail className="text-purple-400" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Email Queue</h3>
              <p className="text-sm text-slate-400">Monitor sent, queued, and failed emails · Retry failures</p>
            </div>
          </div>
          <ChevronDown className="text-purple-400 rotate-[-90deg]" size={24} />
        </div>
      </Link>

      {/* Football Match Day Control Link */}
      <Link
        to="/admin/football/match-day"
        className="block bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-xl p-4 hover:bg-amber-500/20 transition-all"
        data-testid="football-matchday-link"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Globe className="text-amber-400" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">⚽ Football — Match Day Control</h3>
              <p className="text-sm text-slate-400">Trigger scoring runs · Apply live stat overrides (FIFA WC 2026)</p>
            </div>
          </div>
          <ChevronDown className="text-amber-400 rotate-[-90deg]" size={24} />
        </div>
      </Link>

      {/* Football Squads Visibility Link */}
      <Link
        to="/admin/football/squads"
        className="block bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-xl p-4 hover:bg-emerald-500/20 transition-all"
        data-testid="football-squads-link"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Globe className="text-emerald-400" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">⚽ Football — Squads</h3>
              <p className="text-sm text-slate-400">Read-only: locked/draft counts · view any user's XI</p>
            </div>
          </div>
          <ChevronDown className="text-emerald-400 rotate-[-90deg]" size={24} />
        </div>
      </Link>

      {/* Football Weekly Leaderboards Link */}
      <Link
        to="/admin/football/weeks"
        className="block bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4 hover:bg-amber-500/20 transition-all"
        data-testid="football-weeks-link"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Globe className="text-amber-400" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">⚽ Football — Weekly Leaderboards</h3>
              <p className="text-sm text-slate-400">Create weeks · assign match days · auto-suggest by stage</p>
            </div>
          </div>
          <ChevronDown className="text-amber-400 rotate-[-90deg]" size={24} />
        </div>
      </Link>
      {/* Penalty Cup Sponsor Link */}
      <Link
        to="/admin/football/mini-cup-sponsor"
        className="block bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-xl p-4 hover:bg-emerald-500/20 transition-all"
        data-testid="football-minicup-sponsor-link"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Trophy className="text-emerald-400" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">⚽ Penalty Cup — Sponsor</h3>
              <p className="text-sm text-slate-400">Configure mini-game sponsor · branding · click stats</p>
            </div>
          </div>
          <ChevronDown className="text-emerald-400 rotate-[-90deg]" size={24} />
        </div>
      </Link>
      <Link
        to="/admin/audience"
        className="block bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-xl p-4 hover:bg-cyan-500/20 transition-all"
        data-testid="audience-dashboard-link"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
              <Users className="text-cyan-400" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Audience Dashboard</h3>
              <p className="text-sm text-slate-400">Cross-sport demographics & engagement · Export as image</p>
            </div>
          </div>
          <ChevronDown className="text-cyan-400 rotate-[-90deg]" size={24} />
        </div>
      </Link>

      {/* Match Day Control Section - NEW */}
      <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl overflow-hidden">
        <button
          onClick={() => { setShowMatchDaySection(!showMatchDaySection); if (!showMatchDaySection) fetchMdMatches(); }}
          className="w-full flex items-center justify-between p-6 hover:bg-slate-700/20 transition-all"
          data-testid="toggle-matchday-control-section"
        >
          <div className="flex items-center gap-3">
            <Clock className="text-emerald-400" size={24} />
            <div className="text-left">
              <h2 className="text-xl font-semibold text-white">Match Day Control</h2>
              <p className="text-slate-400 text-sm">Manually open / extend the daily team submission window</p>
            </div>
          </div>
          {showMatchDaySection ? <ChevronUp className="text-slate-400" size={24} /> : <ChevronDown className="text-slate-400" size={24} />}
        </button>

        {showMatchDaySection && (
          <div className="px-6 pb-6 border-t border-emerald-500/20">
            <div className="mt-4 mb-4 p-4 bg-slate-900/50 rounded-lg">
              <p className="text-slate-300 text-sm">
                Opening a match day bypasses the automatic kickoff lock and lets users submit/edit their daily XI for the chosen number of minutes. Use this if a match is delayed or you want a longer grace window.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <label className="text-slate-300 text-sm">Keep open for</label>
              <input
                type="number"
                min={1}
                max={720}
                value={mdMinutes}
                onChange={(e) => setMdMinutes(Math.max(1, Math.min(720, parseInt(e.target.value) || 0)))}
                className="w-24 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                data-testid="matchday-minutes-input"
              />
              <span className="text-slate-400 text-sm">minutes</span>
              <button
                onClick={fetchMdMatches}
                className="sm:ml-auto px-3 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-all flex items-center justify-center gap-2 text-sm"
                data-testid="matchday-refresh-btn"
              >
                <RefreshCw size={16} /> Refresh
              </button>
            </div>

            {mdLoading ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-6 justify-center">
                <Loader2 className="animate-spin" size={18} /> Loading matches…
              </div>
            ) : mdMatches.length === 0 ? (
              <p className="text-slate-500 text-sm py-6 text-center">No upcoming or live matches found.</p>
            ) : (
              <div className="space-y-2">
                {mdMatches.map(m => {
                  const openUntil = mdOverrideActive(m);
                  const busy = mdBusyId === m.match_id;
                  return (
                    <div
                      key={m.match_id}
                      className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-slate-900/40 rounded-lg"
                      data-testid={`matchday-row-${m.match_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          M{m.match_number} • {m.team1} vs {m.team2}
                        </p>
                        <p className="text-slate-500 text-xs">
                          {m.date} · {m.time_gmt} GMT · <span className="font-mono">#{m.match_id}</span>
                          {openUntil && (
                            <span className="ml-2 text-emerald-400">
                              • OPEN until {openUntil.toLocaleTimeString()}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setMatchDayOpen(m.match_id, mdMinutes)}
                          disabled={busy}
                          className="px-3 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center gap-2 text-sm"
                          data-testid={`matchday-open-${m.match_id}`}
                        >
                          {busy ? <Loader2 className="animate-spin" size={16} /> : <Clock size={16} />}
                          Open {mdMinutes}m
                        </button>
                        {openUntil && (
                          <button
                            onClick={() => setMatchDayOpen(m.match_id, 0)}
                            disabled={busy}
                            className="px-3 py-2 bg-red-500/20 text-red-400 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-all disabled:opacity-50 flex items-center gap-2 text-sm"
                            data-testid={`matchday-close-${m.match_id}`}
                          >
                            <Lock size={16} /> Close
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Scorecard Sync Section - NEW */}
      <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl overflow-hidden">
        <button
          onClick={() => setShowScorecardSection(!showScorecardSection)}
          className="w-full flex items-center justify-between p-6 hover:bg-slate-700/20 transition-all"
          data-testid="toggle-scorecard-section"
        >
          <div className="flex items-center gap-3">
            <Zap className="text-orange-400" size={24} />
            <div className="text-left">
              <h2 className="text-xl font-semibold text-white">Scorecard Sync & Points</h2>
              <p className="text-slate-400 text-sm">Sync match scorecards and calculate fantasy points</p>
            </div>
          </div>
          {showScorecardSection ? <ChevronUp className="text-slate-400" size={24} /> : <ChevronDown className="text-slate-400" size={24} />}
        </button>
        
        {showScorecardSection && (
          <div className="px-6 pb-6 border-t border-orange-500/20">
            {/* Instructions */}
            <div className="mt-4 mb-4 p-4 bg-slate-900/50 rounded-lg">
              <h4 className="text-orange-400 font-semibold mb-2">📋 How to Sync:</h4>
              <ol className="text-slate-300 text-sm space-y-1 list-decimal list-inside">
                <li>Enter the Cricbuzz Match ID (e.g., 138953, 138963, 138974)</li>
                <li>Click "Check Status" to see if already synced</li>
                <li>Click "Sync Scorecard" to fetch data and calculate points</li>
                <li>Use "Force Re-sync" to recalculate (won't double points)</li>
              </ol>
              <p className="text-amber-400 text-xs mt-2">⚠️ API limit: 100 calls/day. Use wisely!</p>
            </div>
            
            {/* Sync Input */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <input
                type="text"
                value={syncMatchId}
                onChange={(e) => setSyncMatchId(e.target.value)}
                placeholder="Enter Match ID (e.g., 138953)"
                className="flex-1 px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 font-mono"
                data-testid="sync-match-id-input"
              />
              <button
                onClick={checkSyncStatus}
                className="px-4 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-all flex items-center justify-center gap-2"
              >
                <Eye size={18} />
                <span className="sm:inline">Check Status</span>
              </button>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <button
                onClick={() => syncScorecard(false)}
                disabled={syncing || !syncMatchId.trim()}
                className="flex-1 px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="sync-scorecard-btn"
              >
                {syncing ? <Loader2 className="animate-spin" size={18} /> : <RefreshCw size={18} />}
                Sync Scorecard
              </button>
              <button
                onClick={() => syncScorecard(true)}
                disabled={syncing || !syncMatchId.trim()}
                className="px-4 py-3 bg-red-500/20 text-red-400 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <RefreshCw size={18} />
                Force Re-sync
              </button>
            </div>
            
            {/* Recalculate Points */}
            <div className="mb-4 p-3 bg-slate-900/50 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-300 font-medium">Recalculate All Points</p>
                <p className="text-xs text-slate-500">Rebuilds weekly &amp; total points from match data. Fixes any data corruption.</p>
              </div>
              <button
                onClick={backfillWeeklyPoints}
                disabled={syncing}
                className="px-4 py-2 bg-cyan-500/20 text-cyan-400 border border-cyan-500/50 rounded-lg hover:bg-cyan-500/30 transition-all disabled:opacity-50 flex items-center gap-2 text-sm"
                data-testid="backfill-weekly-btn"
              >
                {syncing ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />}
                Recalculate
              </button>
            </div>
            
            {/* Recalculate Daily Points */}
            <div className="mb-4 p-3 bg-slate-900/50 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-300 font-medium">Recalculate Daily Points</p>
                <p className="text-xs text-slate-500">Force-recalculates daily points for the Match ID above. Reverses old scores first.</p>
              </div>
              <button
                onClick={forceRecalcDailyPoints}
                disabled={syncing || !syncMatchId.trim()}
                className="px-4 py-2 bg-orange-500/20 text-orange-400 border border-orange-500/50 rounded-lg hover:bg-orange-500/30 transition-all disabled:opacity-50 flex items-center gap-2 text-sm"
                data-testid="recalc-daily-btn"
              >
                {syncing ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />}
                Recalc Daily
              </button>
            </div>
            
            {/* Fix Orphaned Teams */}
            <div className="mb-4 p-3 bg-slate-900/50 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-300 font-medium">Fix Orphaned Teams</p>
                <p className="text-xs text-slate-500">Sets tournament_id on teams missing it (admin overrides, old data). Run before Recalculate.</p>
              </div>
              <button
                onClick={async () => {
                  setSyncing(true); setError("");
                  try {
                    const res = await axios.post(`${API}/admin/fix-orphaned-teams`, {}, { headers: getAuthHeader() });
                    setSuccess(res.data.message);
                  } catch (e) { setError(e.response?.data?.detail || "Failed"); }
                  finally { setSyncing(false); }
                }}
                disabled={syncing}
                className="px-4 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/50 rounded-lg hover:bg-amber-500/30 transition-all disabled:opacity-50 flex items-center gap-2 text-sm"
                data-testid="fix-orphaned-teams-btn"
              >
                {syncing ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />}
                Fix Teams
              </button>
            </div>
            
            {/* Budget System */}
            <div className="mb-4 p-4 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">💰</span>
                <h4 className="text-white font-semibold">Budget System</h4>
              </div>
              <p className="text-slate-400 text-xs mb-3">Create purchase snapshots for budget calculations. Budget = Snapshot team value + savings + 5 Cr bonus. Run snapshot at end of each week before opening next transfer window.</p>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    setSyncing(true);
                    setError("");
                    try {
                      const res = await axios.post(`${API}/admin/budget/backfill-week1`, {}, { headers: getAuthHeader() });
                      setSuccess(`Week 1 snapshots created! ${res.data.snapshots_created} teams backfilled.`);
                    } catch (e) {
                      setError(e.response?.data?.detail || "Failed to backfill Week 1");
                    } finally {
                      setSyncing(false);
                    }
                  }}
                  disabled={syncing}
                  className="px-4 py-2 bg-emerald-500/20 text-emerald-400 border border-emerald-500/50 rounded-lg hover:bg-emerald-500/30 transition-all disabled:opacity-50 flex items-center gap-2 text-sm"
                  data-testid="backfill-week1-btn"
                >
                  {syncing ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />}
                  Backfill Week 1
                </button>
                <button
                  onClick={async () => {
                    const weekNum = prompt("Enter week number to snapshot:", "2");
                    if (!weekNum) return;
                    setSyncing(true);
                    setError("");
                    try {
                      const res = await axios.post(`${API}/admin/budget/snapshot-week/${weekNum}`, {}, { headers: getAuthHeader() });
                      setSuccess(`Week ${weekNum} snapshots complete! ${res.data.snapshots_created} created, ${res.data.snapshots_updated} updated.`);
                    } catch (e) {
                      setError(e.response?.data?.detail || "Failed to create snapshot");
                    } finally {
                      setSyncing(false);
                    }
                  }}
                  disabled={syncing}
                  className="px-4 py-2 bg-cyan-500/20 text-cyan-400 border border-cyan-500/50 rounded-lg hover:bg-cyan-500/30 transition-all disabled:opacity-50 flex items-center gap-2 text-sm"
                  data-testid="snapshot-week-btn"
                >
                  {syncing ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                  Snapshot Week
                </button>
                <button
                  onClick={async () => {
                    try {
                      const headers = getAuthHeader();
                      const res = await axios.get(`${API}/admin/transfer-window-summary`, { headers });
                      const text = res.data.summary;
                      await navigator.clipboard.writeText(text);
                      setSuccess("Transfer window summary copied to clipboard! Paste into WhatsApp.");
                    } catch (e) {
                      setError(e.response?.data?.detail || "Failed to generate summary");
                    }
                  }}
                  className="px-4 py-2 bg-green-500/20 text-green-400 border border-green-500/50 rounded-lg hover:bg-green-500/30 transition-all disabled:opacity-50 flex items-center gap-2 text-sm"
                  data-testid="copy-whatsapp-summary-btn"
                >
                  <Share2 size={16} />
                  WhatsApp Summary
                </button>
              </div>
            </div>
            
            {/* Purple & Orange Cap Awards */}
            <div className="mb-4 p-4 bg-gradient-to-r from-purple-500/10 to-orange-500/10 border border-purple-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🟣</span>
                <span className="text-lg">🟠</span>
                <h4 className="text-white font-semibold">Purple & Orange Cap</h4>
              </div>
              <p className="text-slate-400 text-xs mb-3">Award Purple Cap (most wickets) and Orange Cap (most runs) for a week. Users with these players in their team get +25 bonus points.</p>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="text-xs text-slate-400 mb-1 block">Week Number</label>
                  <input
                    type="number"
                    min="1"
                    value={capWeekNumber}
                    onChange={(e) => setCapWeekNumber(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-purple-500 font-mono"
                    data-testid="cap-week-input"
                  />
                </div>
                <button
                  onClick={calculateCaps}
                  disabled={syncing}
                  className="px-4 py-2 bg-gradient-to-r from-purple-500 to-orange-500 text-white rounded-lg hover:from-purple-600 hover:to-orange-600 transition-all disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
                  data-testid="calculate-caps-btn"
                >
                  {syncing ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />}
                  Calculate Caps
                </button>
              </div>
              
              {/* Cap Results */}
              {capResult && (
                <div className="mt-3 space-y-2">
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span>🟠</span>
                      <span className="text-orange-400 font-semibold text-sm">Orange Cap - Most Runs ({capResult.orange_cap?.value})</span>
                    </div>
                    {capResult.orange_cap?.winners?.map((w, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-white">{w.player_name}</span>
                        <span className="text-orange-400">{w.runs} runs, {w.users_awarded} users awarded</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span>🟣</span>
                      <span className="text-purple-400 font-semibold text-sm">Purple Cap - Most Wickets ({capResult.purple_cap?.value})</span>
                    </div>
                    {capResult.purple_cap?.winners?.map((w, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-white">{w.player_name}</span>
                        <span className="text-purple-400">{w.wickets} wkts, {w.users_awarded} users awarded</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Sync Result */}
            {syncResult && (
              <div className="bg-slate-900/50 rounded-lg p-4">
                <h4 className="text-emerald-400 font-semibold mb-2">Sync Result:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <div className="bg-slate-800/50 rounded p-2 text-center">
                    <p className="text-2xl font-bold text-white">{syncResult.performances_added || syncResult.performances_count || 0}</p>
                    <p className="text-xs text-slate-400">Performances</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-2 text-center">
                    <p className="text-2xl font-bold text-white">{syncResult.teams_updated || 0}</p>
                    <p className="text-xs text-slate-400">Teams Updated</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-2 text-center">
                    <p className={`text-lg font-bold ${syncResult.scorecard_synced ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {syncResult.scorecard_synced ? '✓ Synced' : '✗ Not Synced'}
                    </p>
                    <p className="text-xs text-slate-400">Status</p>
                  </div>
                  <div className="bg-slate-800/50 rounded p-2 text-center">
                    <p className={`text-lg font-bold ${syncResult.points_calculated ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {syncResult.points_calculated ? '✓ Calculated' : '✗ Pending'}
                    </p>
                    <p className="text-xs text-slate-400">Points</p>
                  </div>
                </div>
                
                {syncResult.match_result && (
                  <p className="text-white text-sm mb-2">📊 Result: {syncResult.match_result}</p>
                )}
                
                {syncResult.top_performers && syncResult.top_performers.length > 0 ? (
                  <div>
                    <p className="text-slate-400 text-sm mb-2">🏆 Top Performers:</p>
                    <div className="space-y-1">
                      {syncResult.top_performers.map((performer, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-slate-800/50 rounded px-3 py-2 text-sm">
                          <span className="text-white font-medium">{performer.player_name || performer.player_id}</span>
                          <div className="flex gap-3 text-xs">
                            {performer.runs > 0 && <span className="text-blue-400">{performer.runs} runs</span>}
                            {performer.wickets > 0 && <span className="text-red-400">{performer.wickets} wkts</span>}
                            {performer.catches > 0 && <span className="text-amber-400">{performer.catches} catch</span>}
                            <span className="text-emerald-400 font-bold">{performer.points} pts</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Player Section */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <button
          onClick={() => setShowAddPlayerSection(!showAddPlayerSection)}
          className="w-full flex items-center justify-between p-6 hover:bg-slate-700/20 transition-all"
          data-testid="toggle-add-player-section"
        >
          <div className="flex items-center gap-3">
            <Users className="text-emerald-400" size={24} />
            <div className="text-left">
              <h2 className="text-xl font-semibold text-white">Add Player</h2>
              <p className="text-slate-400 text-sm">Add new players to the database</p>
            </div>
          </div>
          {showAddPlayerSection ? <ChevronUp className="text-slate-400" size={24} /> : <ChevronDown className="text-slate-400" size={24} />}
        </button>
        
        {showAddPlayerSection && (
          <div className="px-6 pb-6 border-t border-slate-700/50">
            <div className="mt-4 space-y-4">
              {/* Cricbuzz ID (optional) */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Cricbuzz ID (optional - for scorecard sync)</label>
                <input
                  type="text"
                  value={newPlayerCricbuzzId}
                  onChange={(e) => setNewPlayerCricbuzzId(e.target.value)}
                  placeholder="e.g., 15067"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  data-testid="add-player-cricbuzz-id"
                />
              </div>
              
              {/* Player Name */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Player Name *</label>
                <input
                  type="text"
                  value={newPlayerName}
                  onChange={(e) => setNewPlayerName(e.target.value)}
                  placeholder="e.g., Dilshan Madushanka"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  data-testid="add-player-name"
                />
              </div>
              
              {/* Country */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Country *</label>
                <select
                  value={newPlayerCountry}
                  onChange={(e) => setNewPlayerCountry(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  data-testid="add-player-country"
                >
                  <option value="">Select Country</option>
                  <option value="India">India</option>
                  <option value="Australia">Australia</option>
                  <option value="England">England</option>
                  <option value="Pakistan">Pakistan</option>
                  <option value="South Africa">South Africa</option>
                  <option value="New Zealand">New Zealand</option>
                  <option value="Sri Lanka">Sri Lanka</option>
                  <option value="West Indies">West Indies</option>
                  <option value="Bangladesh">Bangladesh</option>
                  <option value="Afghanistan">Afghanistan</option>
                  <option value="Ireland">Ireland</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="Scotland">Scotland</option>
                  <option value="Nepal">Nepal</option>
                  <option value="Oman">Oman</option>
                  <option value="Namibia">Namibia</option>
                  <option value="Uganda">Uganda</option>
                  <option value="USA">USA</option>
                  <option value="Canada">Canada</option>
                  <option value="Papua New Guinea">Papua New Guinea</option>
                </select>
              </div>
              
              {/* Role */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Role *</label>
                <select
                  value={newPlayerRole}
                  onChange={(e) => setNewPlayerRole(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  data-testid="add-player-role"
                >
                  <option value="Batsman">Batsman</option>
                  <option value="Bowler">Bowler</option>
                  <option value="All-Rounder">All-Rounder</option>
                  <option value="Wicket-Keeper">Wicket-Keeper</option>
                </select>
              </div>
              
              {/* Credits (Crore) */}
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Credits (Crore) *</label>
                <input
                  type="number"
                  step="0.5"
                  min="5"
                  max="15"
                  value={newPlayerCredits}
                  onChange={(e) => setNewPlayerCredits(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  data-testid="add-player-credits"
                />
                <p className="text-xs text-slate-500 mt-1">Typical: Batsman 8-10, Bowler 7-9, All-Rounder 8.5-10, WK 7.5-9</p>
              </div>
              
              {/* Add Button */}
              <button
                onClick={async () => {
                  if (!newPlayerName || !newPlayerCountry) {
                    setError("Name and Country are required");
                    return;
                  }
                  setAddingPlayer(true);
                  setAddPlayerResult(null);
                  setError("");
                  try {
                    const res = await axios.post(`${API}/admin/add-player`, {
                      name: newPlayerName,
                      country: newPlayerCountry,
                      role: newPlayerRole,
                      credits: parseFloat(newPlayerCredits),
                      cricbuzz_id: newPlayerCricbuzzId || null
                    }, { headers: getAuthHeader() });
                    setAddPlayerResult(res.data);
                    setSuccess(`Player ${newPlayerName} added successfully!`);
                    // Clear form
                    setNewPlayerName("");
                    setNewPlayerCricbuzzId("");
                  } catch (e) {
                    setError(e.response?.data?.detail || "Failed to add player");
                  } finally {
                    setAddingPlayer(false);
                  }
                }}
                disabled={addingPlayer || !newPlayerName || !newPlayerCountry}
                className="w-full px-4 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                data-testid="add-player-submit"
              >
                {addingPlayer ? <Loader2 className="animate-spin" size={18} /> : <Users size={18} />}
                Add Player
              </button>
              
              {addPlayerResult && (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm">
                  ✓ {addPlayerResult.message}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Super Over Entry Section */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <button
          className="w-full flex items-center justify-between"
          onClick={() => setShowSuperOverSection(!showSuperOverSection)}
          data-testid="super-over-toggle"
        >
          <div className="flex items-center gap-3">
            <Zap className="text-amber-400" size={24} />
            <div>
              <h2 className="text-xl font-semibold text-white text-left">Super Over Entry</h2>
              <p className="text-slate-400 text-sm text-left">Manually add super over performances for matches</p>
            </div>
          </div>
          {showSuperOverSection ? <ChevronUp className="text-slate-400" size={24} /> : <ChevronDown className="text-slate-400" size={24} />}
        </button>

        {showSuperOverSection && (
          <div className="mt-4 space-y-4">
            {/* Match ID & Load Players */}
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="text-xs text-slate-400 mb-1 block">Match ID</label>
                <input
                  type="text"
                  value={soMatchId}
                  onChange={(e) => setSoMatchId(e.target.value)}
                  placeholder="e.g. 139084"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500 font-mono"
                  data-testid="so-match-id-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Super Over #</label>
                <select
                  value={soNumber}
                  onChange={(e) => setSoNumber(parseInt(e.target.value))}
                  className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
                  data-testid="so-number-select"
                >
                  <option value={1}>1st Super Over</option>
                  <option value={2}>2nd Super Over</option>
                  <option value={3}>3rd Super Over</option>
                </select>
              </div>
              <button
                onClick={loadSoMatchPlayers}
                disabled={!soMatchId || soLoadingPlayers}
                className="px-4 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/50 rounded-lg hover:bg-amber-500/30 transition-all disabled:opacity-50 flex items-center gap-2 text-sm"
                data-testid="so-load-players-btn"
              >
                {soLoadingPlayers ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
                Load Players
              </button>
            </div>

            {/* Existing Super Over Data */}
            {soExistingData && Object.keys(soExistingData).length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-amber-400 text-sm font-medium mb-2">Existing Super Over Data:</p>
                {Object.entries(soExistingData).map(([soNum, perfs]) => (
                  <div key={soNum} className="mb-2">
                    <p className="text-slate-300 text-xs font-medium">Super Over {soNum}:</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {perfs.map((p, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-slate-800 rounded text-slate-300">
                          {p.player_name} ({p.player_team}): {p.points} pts
                          {p.runs > 0 && ` ${p.runs}(${p.balls_faced}b)`}
                          {p.wickets > 0 && ` ${p.wickets}w`}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
                <p className="text-xs text-slate-500 mt-1">Re-submitting for the same super over # will replace existing data.</p>
              </div>
            )}

            {/* Player Entry Forms */}
            {soMatchPlayers.length > 0 && (
              <div className="space-y-4">
                {/* Batsmen */}
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-medium text-sm">Batsmen</h4>
                    <button
                      onClick={() => setSoBatsmen([...soBatsmen, { player_id: "", runs: 0, balls: 0, fours: 0, sixes: 0, is_out: false }])}
                      className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30"
                    >
                      + Add Batsman
                    </button>
                  </div>
                  {soBatsmen.map((bat, i) => (
                    <div key={i} className="grid grid-cols-2 sm:grid-cols-8 gap-2 mb-3 sm:mb-2 items-center bg-slate-800/50 sm:bg-transparent p-2 sm:p-0 rounded-lg">
                      <select
                        value={bat.player_id}
                        onChange={(e) => updateSoBatsman(i, "player_id", e.target.value)}
                        className="col-span-2 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs"
                        data-testid={`so-bat-select-${i}`}
                      >
                        <option value="">Select Player</option>
                        {soMatchPlayers.map(p => <option key={p.id} value={p.id}>{p.name} ({p.team})</option>)}
                      </select>
                      <input type="number" min="0" value={bat.runs} onChange={(e) => updateSoBatsman(i, "runs", parseInt(e.target.value) || 0)} className="px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs" placeholder="Runs" title="Runs" />
                      <input type="number" min="0" value={bat.balls} onChange={(e) => updateSoBatsman(i, "balls", parseInt(e.target.value) || 0)} className="px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs" placeholder="Balls" title="Balls" />
                      <input type="number" min="0" value={bat.fours} onChange={(e) => updateSoBatsman(i, "fours", parseInt(e.target.value) || 0)} className="px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs" placeholder="4s" title="4s" />
                      <input type="number" min="0" value={bat.sixes} onChange={(e) => updateSoBatsman(i, "sixes", parseInt(e.target.value) || 0)} className="px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs" placeholder="6s" title="6s" />
                      <label className="flex items-center gap-1 text-xs text-slate-400">
                        <input type="checkbox" checked={bat.is_out} onChange={(e) => updateSoBatsman(i, "is_out", e.target.checked)} className="accent-red-500" /> Out
                      </label>
                      <button onClick={() => setSoBatsmen(soBatsmen.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-300 justify-self-end sm:justify-self-auto"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <div className="text-[10px] text-slate-500 mt-1">Runs | Balls | 4s | 6s | Out?</div>
                </div>

                {/* Bowlers */}
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-medium text-sm">Bowlers</h4>
                    <button
                      onClick={() => setSoBowlers([...soBowlers, { player_id: "", overs: 1, runs: 0, wickets: 0, maidens: 0 }])}
                      className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30"
                    >
                      + Add Bowler
                    </button>
                  </div>
                  {soBowlers.map((bow, i) => (
                    <div key={i} className="grid grid-cols-2 sm:grid-cols-7 gap-2 mb-3 sm:mb-2 items-center bg-slate-800/50 sm:bg-transparent p-2 sm:p-0 rounded-lg">
                      <select
                        value={bow.player_id}
                        onChange={(e) => updateSoBowler(i, "player_id", e.target.value)}
                        className="col-span-2 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs"
                        data-testid={`so-bowl-select-${i}`}
                      >
                        <option value="">Select Player</option>
                        {soMatchPlayers.map(p => <option key={p.id} value={p.id}>{p.name} ({p.team})</option>)}
                      </select>
                      <input type="number" min="0" step="0.1" value={bow.overs} onChange={(e) => updateSoBowler(i, "overs", parseFloat(e.target.value) || 0)} className="px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs" placeholder="Overs" title="Overs" />
                      <input type="number" min="0" value={bow.runs} onChange={(e) => updateSoBowler(i, "runs", parseInt(e.target.value) || 0)} className="px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs" placeholder="Runs" title="Runs Conceded" />
                      <input type="number" min="0" value={bow.wickets} onChange={(e) => updateSoBowler(i, "wickets", parseInt(e.target.value) || 0)} className="px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs" placeholder="Wkts" title="Wickets" />
                      <input type="number" min="0" value={bow.maidens} onChange={(e) => updateSoBowler(i, "maidens", parseInt(e.target.value) || 0)} className="px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs" placeholder="Maidens" title="Maidens" />
                      <button onClick={() => setSoBowlers(soBowlers.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-300 justify-self-end sm:justify-self-auto"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <div className="text-[10px] text-slate-500 mt-1">Overs | Runs | Wickets | Maidens</div>
                </div>

                {/* Fielding */}
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-medium text-sm">Fielding Contributions</h4>
                    <button
                      onClick={() => setSoFielding([...soFielding, { player_id: "", catches: 0, stumpings: 0, run_outs: 0 }])}
                      className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30"
                    >
                      + Add Fielder
                    </button>
                  </div>
                  {soFielding.map((f, i) => (
                    <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 mb-3 sm:mb-2 items-center bg-slate-800/50 sm:bg-transparent p-2 sm:p-0 rounded-lg">
                      <select
                        value={f.player_id}
                        onChange={(e) => { const u = [...soFielding]; u[i] = { ...u[i], player_id: e.target.value }; setSoFielding(u); }}
                        className="col-span-2 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs"
                      >
                        <option value="">Select Player</option>
                        {soMatchPlayers.map(p => <option key={p.id} value={p.id}>{p.name} ({p.team})</option>)}
                      </select>
                      <input type="number" min="0" value={f.catches} onChange={(e) => { const u = [...soFielding]; u[i] = { ...u[i], catches: parseInt(e.target.value) || 0 }; setSoFielding(u); }} className="px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs" placeholder="Catches" title="Catches" />
                      <input type="number" min="0" value={f.stumpings} onChange={(e) => { const u = [...soFielding]; u[i] = { ...u[i], stumpings: parseInt(e.target.value) || 0 }; setSoFielding(u); }} className="px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs" placeholder="Stumpings" title="Stumpings" />
                      <input type="number" min="0" value={f.run_outs} onChange={(e) => { const u = [...soFielding]; u[i] = { ...u[i], run_outs: parseInt(e.target.value) || 0 }; setSoFielding(u); }} className="px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-white text-xs" placeholder="Run Outs" title="Run Outs" />
                      <button onClick={() => setSoFielding(soFielding.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-300 justify-self-end sm:justify-self-auto"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <div className="text-[10px] text-slate-500 mt-1">Catches | Stumpings | Run Outs</div>
                </div>

                {/* Submit Button */}
                <button
                  onClick={submitSuperOver}
                  disabled={soSubmitting}
                  className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="so-submit-btn"
                >
                  {soSubmitting ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                  Save Super Over {soNumber} Data
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Transfer Windows Section */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Calendar className="text-cyan-400" size={24} />
            <div>
              <h2 className="text-xl font-semibold text-white">Transfer Windows</h2>
              {activeTournament && (
                <p className="text-sm text-cyan-400 font-medium mt-0.5">
                  {activeTournament.name || activeTournament.tournament_id || "Active Tournament"}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-all"
            data-testid="create-window-btn"
          >
            <Plus size={18} />
            Create Window
          </button>
        </div>

        {/* Tournament Context Banner */}
        {activeTournament && (
          <div className="mb-4 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
            <p className="text-sm text-cyan-300">
              Transfer windows for: <strong>{activeTournament.name || activeTournament.tournament_id}</strong>
            </p>
            {windows.length === 0 && (
              <p className="text-xs text-slate-400 mt-1">
                No transfer windows created yet. Click "Create Window" to set up Week 1 for {activeTournament.name || "this tournament"}.
              </p>
            )}
          </div>
        )}

        {windows.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Calendar className="mx-auto mb-4 opacity-50" size={40} />
            <p className="mb-3">No transfer windows created yet for {activeTournament?.name || "this tournament"}</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-400 border border-emerald-500/50 rounded-lg hover:bg-emerald-500/30 transition-all"
              data-testid="create-first-window-btn"
            >
              <Plus size={18} />
              Create Week 1 Window
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {windows.map((tw) => (
              <div 
                key={tw.id}
                className={`p-4 rounded-xl border transition-all ${
                  tw.is_active 
                    ? "bg-emerald-500/10 border-emerald-500/50" 
                    : "bg-slate-900/50 border-slate-700"
                }`}
                data-testid={`transfer-window-${tw.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      {tw.name}
                      <span className="text-xs px-2 py-0.5 bg-slate-600 text-slate-300 rounded-full">Week {tw.week_number}</span>
                      {tw.is_active && (
                        <span className="text-xs px-2 py-0.5 bg-emerald-500 text-white rounded-full">ACTIVE</span>
                      )}
                      {tw.new_users_only && (
                        <span className="text-xs px-2 py-0.5 bg-amber-500 text-black rounded-full">NEW USERS ONLY</span>
                      )}
                      {tw.finals_mode && (
                        <span className="text-xs px-2 py-0.5 bg-red-500 text-white rounded-full">FINALS MODE</span>
                      )}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {tw.start_date} - {tw.end_date}
                      </span>
                      <span className="text-xs text-cyan-400">
                        {(tw.match_ids || []).length} matches assigned
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        if (editingWindowMatches === tw.id) {
                          setEditingWindowMatches(null);
                        } else {
                          setEditingWindowMatches(tw.id);
                          setWindowMatchIds(tw.match_ids || []);
                          fetchCompletedMatches();
                        }
                      }}
                      className="px-3 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-all text-sm"
                      data-testid={`assign-matches-btn-${tw.id}`}
                    >
                      <Edit3 size={14} className="inline mr-1" />
                      Matches
                    </button>
                    <button
                      onClick={() => toggleWindow(tw.id, !tw.is_active)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        tw.is_active 
                          ? "bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30" 
                          : "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                      }`}
                    >
                      {tw.is_active ? "Deactivate" : "Activate"}
                    </button>
                    <button
                      onClick={() => deleteWindow(tw.id)}
                      className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                
                {/* Match Assignment Panel */}
                {editingWindowMatches === tw.id && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <h4 className="text-sm font-medium text-slate-300 mb-2">Assign Matches to {tw.name}</h4>
                    <div className="max-h-60 overflow-y-auto space-y-1 mb-3">
                      {completedMatches.length === 0 ? (
                        <p className="text-slate-500 text-sm py-2">Loading matches...</p>
                      ) : (
                        completedMatches.map(m => {
                          const isAssigned = windowMatchIds.includes(m.match_id);
                          const isCompleted = m.status === "completed";
                          return (
                            <label key={m.match_id} className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm ${isAssigned ? 'bg-emerald-500/10' : 'hover:bg-slate-800/50'}`}>
                              <input
                                type="checkbox"
                                checked={isAssigned}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setWindowMatchIds([...windowMatchIds, m.match_id]);
                                  } else {
                                    setWindowMatchIds(windowMatchIds.filter(id => id !== m.match_id));
                                  }
                                }}
                                className="accent-emerald-500"
                              />
                              <span className="text-white flex-1">M{m.match_number} <span className="text-slate-400 text-xs font-mono">(#{m.match_id})</span>: {m.team1} vs {m.team2}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${isCompleted ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/50 text-slate-400'}`}>
                                {isCompleted ? 'done' : 'upcoming'}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => saveWindowMatches(tw.id)}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 text-sm flex items-center gap-1"
                      >
                        <Save size={14} /> Save ({windowMatchIds.length} matches)
                      </button>
                      <button
                        onClick={() => setEditingWindowMatches(null)}
                        className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Admin Management Section */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Users className="text-purple-400" size={24} />
          <h2 className="text-xl font-semibold text-white">Admin Management</h2>
        </div>

        {/* Add Admin Form */}
        <form onSubmit={grantAdmin} className="flex gap-3 mb-6">
          <input
            type="email"
            value={newAdminEmail}
            onChange={(e) => setNewAdminEmail(e.target.value)}
            placeholder="Enter user email to grant admin access"
            className="flex-1 px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all"
          >
            Grant Admin
          </button>
        </form>

        {/* Admin List */}
        <div className="space-y-2">
          <p className="text-sm text-slate-400 mb-3">Current Admins ({admins.length})</p>
          {admins.length === 0 ? (
            <p className="text-slate-500 text-center py-4">No admins configured</p>
          ) : (
            admins.map((admin) => (
              <div 
                key={admin.id}
                className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
              >
                <div>
                  <p className="text-white">{admin.display_name || admin.email}</p>
                  <p className="text-slate-400 text-sm">{admin.email}</p>
                </div>
                <button
                  onClick={() => revokeAdmin(admin.email)}
                  className="text-red-400 hover:text-red-300 text-sm"
                >
                  Revoke
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Predictor Admin Management Section */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="text-amber-400" size={24} />
          <div>
            <h2 className="text-xl font-semibold text-white">Predictor Admin Management</h2>
            <p className="text-slate-400 text-sm">Predictor admins can manage predictions but cannot access the full admin panel</p>
          </div>
        </div>

        {/* Add Predictor Admin Form */}
        <form onSubmit={grantPredictorAdmin} className="flex gap-3 mb-6">
          <input
            type="email"
            value={newPredictorAdminEmail}
            onChange={(e) => setNewPredictorAdminEmail(e.target.value)}
            placeholder="Enter user email to grant predictor admin access"
            className="flex-1 px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-500"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all"
          >
            Grant Access
          </button>
        </form>

        {/* Predictor Admin List */}
        <div className="space-y-2">
          <p className="text-sm text-slate-400 mb-3">Current Predictor Admins ({predictorAdmins.length})</p>
          {predictorAdmins.length === 0 ? (
            <p className="text-slate-500 text-center py-4">No predictor admins configured</p>
          ) : (
            predictorAdmins.map((admin) => (
              <div 
                key={admin.id}
                className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
              >
                <div>
                  <p className="text-white">{admin.display_name || admin.email}</p>
                  <p className="text-slate-400 text-sm">{admin.email}</p>
                </div>
                <button
                  onClick={() => revokePredictorAdmin(admin.email)}
                  className="text-red-400 hover:text-red-300 text-sm"
                >
                  Revoke
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* User Management Section (NEW) */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <button
          onClick={toggleUsersSection}
          className="w-full flex items-center justify-between p-6 hover:bg-slate-700/30 transition-all"
          data-testid="toggle-users-section"
        >
          <div className="flex items-center gap-3">
            <Users className="text-amber-400" size={24} />
            <div className="text-left">
              <h2 className="text-xl font-semibold text-white">User Management</h2>
              <p className="text-slate-400 text-sm">View all registered users, teams, and referrals</p>
            </div>
          </div>
          {showUsersSection ? <ChevronUp className="text-slate-400" size={24} /> : <ChevronDown className="text-slate-400" size={24} />}
        </button>
        
        {showUsersSection && (
          <div className="px-6 pb-6 border-t border-slate-700/50">
            {/* Search */}
            <div className="relative mt-4 mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                placeholder="Search by name, email, team, or referral code..."
                className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                data-testid="user-search"
              />
            </div>
            
            {usersLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-emerald-400" size={32} />
              </div>
            ) : (
              <>
                <p className="text-slate-400 text-sm mb-4">
                  Showing {filteredUsers.length} of {allUsers.length} users
                </p>

                {/* Bulk email action bar */}
                {selectedUserIds.size > 0 && (
                  <div className="flex items-center gap-3 mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <span className="text-emerald-400 text-sm font-medium">{selectedUserIds.size} selected</span>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Send onboarding email to ${selectedUserIds.size} selected users?`)) return;
                        try {
                          const headers = await getAuthHeader();
                          const res = await axios.post(
                            `${API}/admin/email/send-to-selected`,
                            { user_ids: Array.from(selectedUserIds) },
                            { headers }
                          );
                          setSuccess(`Sent to ${res.data.sent} users`);
                          setSelectedUserIds(new Set());
                        } catch (e) {
                          setError(e.response?.data?.detail || "Failed to send emails");
                        }
                      }}
                      className="px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-all text-sm font-semibold flex items-center gap-2"
                      data-testid="bulk-email-send-btn"
                    >
                      <Mail size={14} />
                      Send email to {selectedUserIds.size} selected
                    </button>
                    <button
                      onClick={() => setSelectedUserIds(new Set())}
                      className="px-3 py-2 text-slate-400 hover:text-white text-sm transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                )}
                
                {/* Users Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="py-3 px-2 w-8">
                          <input
                            type="checkbox"
                            checked={selectedUserIds.size === filteredUsers.length && filteredUsers.length > 0}
                            onChange={toggleSelectAll}
                            className="rounded"
                            data-testid="select-all-users-checkbox"
                          />
                        </th>
                        <th className="text-left py-3 px-2 text-slate-400 font-medium">User</th>
                        <th className="text-left py-3 px-2 text-slate-400 font-medium">Team</th>
                        <th className="text-center py-3 px-2 text-slate-400 font-medium">Points</th>
                        <th className="text-left py-3 px-2 text-slate-400 font-medium">My Code</th>
                        <th className="text-left py-3 px-2 text-slate-400 font-medium">Used Code</th>
                        <th className="text-center py-3 px-2 text-slate-400 font-medium">Referrals</th>
                        <th className="text-center py-3 px-2 text-slate-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => {
                        const displayName = user.display_name || user.email || "?";
                        const initial = displayName[0]?.toUpperCase() || "?";
                        return (
                        <tr key={user.id} className="border-b border-slate-700/50 hover:bg-slate-700/20">
                          <td className="py-3 px-2">
                            <input
                              type="checkbox"
                              checked={selectedUserIds.has(user.id)}
                              onChange={() => toggleUserSelect(user.id)}
                              className="rounded"
                              data-testid={`select-user-${user.id}`}
                            />
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              {user.photo_url && user.photo_url.startsWith('data:image') ? (
                                <img src={user.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white text-xs font-bold">
                                  {initial}
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="text-white truncate max-w-[150px]">{user.display_name || "No name"}</p>
                                <p className="text-slate-500 text-xs truncate max-w-[150px]">{user.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            {user.team_submitted ? (
                              <span className="text-emerald-400">{user.team_name}</span>
                            ) : (
                              <span className="text-slate-500">No team</span>
                            )}
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className="text-amber-400 font-mono font-bold">{user.total_points || 0}</span>
                          </td>
                          <td className="py-3 px-2">
                            <span className="font-mono text-xs text-cyan-400">{user.referral_code}</span>
                          </td>
                          <td className="py-3 px-2">
                            {user.referred_by ? (
                              <div>
                                <span className="font-mono text-xs text-purple-400">{user.referred_by}</span>
                                {user.referrer_name && (
                                  <p className="text-slate-500 text-xs">({user.referrer_name})</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-500">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              user.referral_count >= 3 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-300'
                            }`}>
                              {user.referral_count || 0}/3
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {user.team_submitted && (
                                <button
                                  onClick={() => {
                                    setSelectedUser(user);
                                    viewUserTeam(user.id);
                                  }}
                                  className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-all"
                                  title="View Team"
                                >
                                  <Eye size={16} />
                                </button>
                              )}
                              {user.team_submitted && (
                                <button
                                  onClick={() => loadTeamForEdit(user.id)}
                                  className="p-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-all"
                                  title="Edit Team"
                                  data-testid={`edit-team-${user.id}`}
                                >
                                  <Settings size={16} />
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setNameEditUser(user);
                                  setNameEditDisplayName(user.display_name || "");
                                  setNameEditTeamName(user.preferred_team_name || user.team_name || "");
                                  setShowNameEditModal(true);
                                }}
                                className="p-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-all"
                                title="Edit Name"
                                data-testid={`edit-name-${user.id}`}
                              >
                                <Edit3 size={16} />
                              </button>
                              <button
                                onClick={() => openPurchaseModal(user)}
                                className="p-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 transition-all"
                                title="League Purchases"
                                data-testid={`view-purchases-${user.id}`}
                              >
                                <ShoppingCart size={16} />
                              </button>
                              <button
                                onClick={() => {
                                  setAdjustUserId(user.id);
                                  setShowAdjustModal(true);
                                }}
                                className="p-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-all"
                                title="Adjust Points"
                              >
                                <Zap size={16} />
                              </button>
                              {user.email && (
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Send onboarding email to ${user.email}?`)) return;
                                    try {
                                      const headers = await getAuthHeader();
                                      await axios.post(
                                        `${API}/admin/email/send-to-user/${user.id}`,
                                        {},
                                        { headers }
                                      );
                                      setSuccess(`Email sent to ${user.email}`);
                                    } catch (e) {
                                      setError(e.response?.data?.detail || "Failed to send email");
                                    }
                                  }}
                                  className="p-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-all"
                                  title="Send onboarding email"
                                  data-testid={`send-email-${user.id}`}
                                >
                                  <Mail size={16} />
                                </button>
                              )}
                              {!user.is_admin && (
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Delete ${user.email}? This will remove them from the database and Firebase.`)) return;
                                    try {
                                      await axios.delete(`${API}/admin/users/${user.id}`, { headers: getAuthHeader() });
                                      setSuccess(`Deleted ${user.email}`);
                                      fetchAllUsers();
                                    } catch (e) {
                                      setError(e.response?.data?.detail || "Failed to delete user");
                                    }
                                  }}
                                  className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all"
                                  title="Delete User"
                                  data-testid={`delete-user-${user.id}`}
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Admin Team Editor Modal */}
      {editTeamUserId && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-team-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">Edit Team (Admin Override)</h2>
              <button onClick={() => { setEditTeamUserId(""); setEditTeamData(null); setEditTeamPlayers([]); }} className="text-slate-400 hover:text-white"><X size={22} /></button>
            </div>

            {editTeamLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="animate-spin text-emerald-400" size={32} /></div>
            ) : (
              <>
                {/* Team Name */}
                <input
                  type="text"
                  value={editTeamName}
                  onChange={e => setEditTeamName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm mb-3"
                  placeholder="Team name"
                  data-testid="edit-team-name"
                />

                {/* Current players */}
                <p className="text-xs text-slate-400 mb-2">Players ({editTeamPlayers.length}/11)</p>
                <div className="space-y-1.5 mb-3">
                  {editTeamPlayers.map(p => (
                    <div key={p.id} className="flex items-center justify-between bg-slate-900/50 border border-slate-700/50 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-white text-sm truncate">{p.name}</span>
                        <span className="text-slate-500 text-xs flex-shrink-0">{p.country} &bull; {p.role}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        <button
                          onClick={() => { setEditTeamCaptain(p.id); if (editTeamVC === p.id) setEditTeamVC(""); }}
                          className={`px-2 py-0.5 rounded text-xs font-bold ${editTeamCaptain === p.id ? "bg-yellow-500 text-black" : "bg-slate-700 text-slate-400 hover:text-white"}`}
                        >C</button>
                        <button
                          onClick={() => { setEditTeamVC(p.id); if (editTeamCaptain === p.id) setEditTeamCaptain(""); }}
                          className={`px-2 py-0.5 rounded text-xs font-bold ${editTeamVC === p.id ? "bg-cyan-500 text-black" : "bg-slate-700 text-slate-400 hover:text-white"}`}
                        >VC</button>
                        <button onClick={() => removeFromEditTeam(p.id)} className="text-red-400 hover:text-red-300 ml-1"><X size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Search to add */}
                {editTeamPlayers.length < 11 && (
                  <div className="relative mb-3">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="text"
                      value={editTeamSearch}
                      onChange={e => setEditTeamSearch(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }}
                      placeholder="Search player to add..."
                      className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder:text-slate-600"
                      data-testid="edit-team-search"
                    />
                    {editTeamSearch.length >= 2 && (
                      <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-slate-800 border border-slate-700 rounded-lg shadow-xl">
                        {(editTeamAllPlayers || [])
                          .filter(p => p?.name?.toLowerCase().includes(editTeamSearch.toLowerCase()) && !editTeamPlayers.find(ep => ep.id === p.id))
                          .slice(0, 10)
                          .map(p => (
                            <button
                              key={p.id}
                              onClick={() => addToEditTeam(p)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-700 transition-colors flex justify-between"
                            >
                              <span className="text-white">{p.name}</span>
                              <span className="text-slate-500 text-xs">{p.country} &bull; {p.role}</span>
                            </button>
                          ))
                        }
                      </div>
                    )}
                  </div>
                )}

                {/* Save */}
                <button
                  onClick={saveEditTeam}
                  disabled={editTeamSaving || editTeamPlayers.length !== 11}
                  className="w-full py-2.5 bg-emerald-500 text-white rounded-lg font-semibold hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                  data-testid="save-edit-team"
                >
                  {editTeamSaving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                  Save Team (Admin Override)
                </button>

                {/* Recalculate for specific match */}
                {editTeamData && (
                  <div className="mt-3 pt-3 border-t border-slate-700/50">
                    <p className="text-xs text-slate-400 mb-2">Recalculate points for a specific match (after team edit):</p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={recalcMatchId}
                        onChange={e => setRecalcMatchId(e.target.value)}
                        placeholder="Match ID (e.g. 117414)"
                        className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
                        data-testid="recalc-match-id"
                      />
                      <button
                        onClick={async () => {
                          if (!recalcMatchId) { setError("Enter a match ID"); return; }
                          try {
                            const res = await axios.post(`${API}/admin/recalculate-user-points/${recalcMatchId}/${editTeamUserId}`, {}, { headers: getAuthHeader() });
                            const d = res.data;
                            setRecalcPreview(d);
                            setSuccess(`Preview: ${d.new_points} pts from new team`);
                          } catch (e) { setError(e.response?.data?.detail || "Failed to preview"); }
                        }}
                        className="px-4 py-2 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-lg text-sm font-medium hover:bg-amber-500/30 transition-all"
                      >Preview</button>
                    </div>

                    {recalcPreview && (
                      <div className="mt-2 bg-slate-900/50 border border-slate-700/50 rounded-lg p-3">
                        <p className="text-white text-sm font-medium mb-1">New points: <span className="text-emerald-400">{recalcPreview.new_points}</span></p>
                        <div className="space-y-0.5 mb-2">
                          {(recalcPreview.breakdown || []).map((b, i) => (
                            <div key={i} className="flex justify-between text-xs">
                              <span className="text-slate-400">{b.player}</span>
                              <span className="text-white font-mono">{b.points}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2 items-center">
                          <input
                            type="number"
                            value={recalcOldPoints}
                            onChange={e => setRecalcOldPoints(e.target.value)}
                            placeholder="Old points to replace"
                            className="flex-1 px-3 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                          />
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Replace ${recalcOldPoints} pts with ${recalcPreview.new_points} pts? Diff: ${(recalcPreview.new_points - parseFloat(recalcOldPoints || 0)).toFixed(1)}`)) return;
                              try {
                                const res = await axios.put(`${API}/admin/apply-user-points/${recalcMatchId}/${editTeamUserId}`, {
                                  old_points: parseFloat(recalcOldPoints || 0)
                                }, { headers: getAuthHeader() });
                                setSuccess(`Applied! Old: ${res.data.old_points}, New: ${res.data.new_points}, Diff: ${res.data.diff}`);
                                setRecalcPreview(null);
                              } catch (e) { setError(e.response?.data?.detail || "Failed to apply"); }
                            }}
                            className="px-4 py-1.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-all"
                          >Apply</button>
                        </div>
                        <p className="text-slate-500 text-xs mt-1">Enter what was previously awarded to this user for this match, then click Apply.</p>
                      </div>
                    )}

                    {/* Quick Fix: Set weekly points directly */}
                    <div className="mt-3 pt-3 border-t border-slate-700/30">
                      <p className="text-xs text-amber-400 font-medium mb-2">Quick Fix: Set weekly points directly (won't touch global)</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={fixWeekKey}
                          onChange={e => setFixWeekKey(e.target.value)}
                          placeholder="e.g. week_8"
                          className="flex-1 px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
                        />
                        <input
                          type="number"
                          value={fixWeekPts}
                          onChange={e => setFixWeekPts(e.target.value)}
                          placeholder="Points"
                          className="w-24 px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
                        />
                        <button
                          onClick={async () => {
                            if (!fixWeekKey || !fixWeekPts) { setError("Enter week key and points"); return; }
                            if (!window.confirm(`Set ${fixWeekKey} = ${fixWeekPts} pts? (Global points NOT changed)`)) return;
                            try {
                              await axios.put(`${API}/admin/fix-weekly-points/${editTeamUserId}`, {
                                week_key: fixWeekKey, points: parseFloat(fixWeekPts)
                              }, { headers: getAuthHeader() });
                              setSuccess(`Weekly points fixed: ${fixWeekKey} = ${fixWeekPts}`);
                            } catch (e) { setError(e.response?.data?.detail || "Failed"); }
                          }}
                          className="px-4 py-1.5 bg-amber-500 text-black rounded-lg text-sm font-medium hover:bg-amber-400 transition-all"
                        >Set</button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Points Adjustment Modal */}
      {showAdjustModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Adjust Points</h2>
              <button onClick={() => setShowAdjustModal(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={adjustUserPoints} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">User ID</label>
                <input
                  type="text"
                  value={adjustUserId}
                  onChange={(e) => setAdjustUserId(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Points (+ to add, - to subtract)</label>
                <input
                  type="number"
                  step="0.5"
                  value={adjustPoints}
                  onChange={(e) => setAdjustPoints(e.target.value)}
                  placeholder="e.g., 25 or -10"
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Reason</label>
                <textarea
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder="Reason for adjustment..."
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white h-24 resize-none"
                  required
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowAdjustModal(false)}
                  className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                >
                  Apply Adjustment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Name Edit Modal */}
      {showNameEditModal && nameEditUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md" data-testid="name-edit-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Edit User Name</h2>
              <button onClick={() => { setShowNameEditModal(false); setNameEditUser(null); }} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="mb-4 p-3 bg-slate-900/50 rounded-lg">
              <p className="text-slate-400 text-sm">Editing: <span className="text-white">{nameEditUser.email}</span></p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Display Name (Person's Name)</label>
                <input
                  type="text"
                  value={nameEditDisplayName}
                  onChange={(e) => setNameEditDisplayName(e.target.value)}
                  placeholder="e.g., John Doe"
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                  data-testid="name-edit-display-name"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Preferred Team Name</label>
                <input
                  type="text"
                  value={nameEditTeamName}
                  onChange={(e) => setNameEditTeamName(e.target.value)}
                  placeholder="e.g., Super Kings XI"
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white"
                  data-testid="name-edit-team-name"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowNameEditModal(false); setNameEditUser(null); }}
                  className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveNameEdit}
                  disabled={nameEditSaving}
                  className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="name-edit-save"
                >
                  {nameEditSaving && <Loader2 className="animate-spin" size={16} />}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Team Modal */}
      {selectedUser && selectedUserTeam && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">{selectedUser.team_name || "Team Details"}</h2>
                <p className="text-slate-400 text-sm">{selectedUser.display_name || selectedUser.email}</p>
              </div>
              <button onClick={() => { setSelectedUser(null); setSelectedUserTeam(null); }} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            {selectedUserTeam.team ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-slate-900/50 rounded-lg p-3">
                    <p className="text-slate-400">Total Points</p>
                    <p className="text-2xl font-bold text-emerald-400">{selectedUserTeam.team.total_points || 0}</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-3">
                    <p className="text-slate-400">Team Created</p>
                    <p className="text-white">{selectedUserTeam.team.created_at ? selectedUserTeam.team.created_at.split('T')[0] : 'N/A'}</p>
                  </div>
                </div>
                
                {/* Captain & VC */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedUserTeam.captain && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                      <p className="text-yellow-400 text-xs font-medium mb-1">CAPTAIN (2x)</p>
                      <p className="text-white font-semibold">{selectedUserTeam.captain.name}</p>
                      <p className="text-slate-400 text-sm">{selectedUserTeam.captain.country} • {selectedUserTeam.captain.role}</p>
                    </div>
                  )}
                  {selectedUserTeam.vice_captain && (
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                      <p className="text-cyan-400 text-xs font-medium mb-1">VICE CAPTAIN (1.5x)</p>
                      <p className="text-white font-semibold">{selectedUserTeam.vice_captain.name}</p>
                      <p className="text-slate-400 text-sm">{selectedUserTeam.vice_captain.country} • {selectedUserTeam.vice_captain.role}</p>
                    </div>
                  )}
                </div>
                
                {/* All Players */}
                <div>
                  <p className="text-slate-400 text-sm mb-2">Squad ({selectedUserTeam.players?.length || 0} players)</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedUserTeam.players?.map((player) => {
                      const playerName = player.name || "";
                      const initials = playerName.split(' ').slice(0, 2).map(word => word[0] || '').join('');
                      return (
                        <div key={player.id} className="flex items-center gap-2 bg-slate-900/50 rounded-lg p-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white text-xs font-bold">
                            {initials}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-sm truncate">{playerName}</p>
                            <p className="text-slate-500 text-xs">{player.country} • {player.role}</p>
                          </div>
                          <span className="text-emerald-400 text-xs font-mono">{player.base_credits}Cr</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">No team found</p>
            )}
          </div>
        </div>
      )}

      {/* Purchase Management Modal */}
      {showPurchaseModal && purchaseModalUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white" data-testid="purchase-modal-title">League Purchases</h2>
                <p className="text-slate-400 text-sm">{purchaseModalUser.display_name || purchaseModalUser.email}</p>
              </div>
              <button onClick={() => { setShowPurchaseModal(false); setPurchaseModalUser(null); setPurchaseData(null); }} className="text-slate-400 hover:text-white" data-testid="purchase-modal-close">
                <X size={24} />
              </button>
            </div>

            {purchaseLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-orange-400" size={32} />
              </div>
            ) : purchaseData ? (
              <div className="space-y-6">
                {/* League Size Purchases */}
                <div>
                  <h3 className="text-sm font-semibold text-orange-400 mb-2 uppercase tracking-wider" data-testid="size-purchases-heading">Size Upgrades ({purchaseData.league_size_purchases?.length || 0})</h3>
                  {purchaseData.league_size_purchases?.length > 0 ? (
                    <div className="space-y-2">
                      {purchaseData.league_size_purchases.map((p, i) => (
                        <div key={i} className="bg-slate-900/60 rounded-lg p-3 flex items-center justify-between" data-testid={`size-purchase-${i}`}>
                          <div>
                            <p className="text-white text-sm font-medium">{p.product_id}</p>
                            <p className="text-slate-500 text-xs">
                              {p.purchased_at ? new Date(p.purchased_at).toLocaleString() : "N/A"}
                              {p.source === "admin_grant" && <span className="ml-1 text-amber-400">(Admin Grant)</span>}
                            </p>
                          </div>
                          <div className="text-right">
                            {p.used_for ? (
                              <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full">Used: {p.used_for.substring(0, 8)}...</span>
                            ) : (
                              <span className="text-xs px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full">Unused</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No size upgrade purchases</p>
                  )}
                </div>

                {/* League Slot Purchases */}
                <div>
                  <h3 className="text-sm font-semibold text-purple-400 mb-2 uppercase tracking-wider" data-testid="slot-purchases-heading">Slot Purchases ({purchaseData.league_purchases?.length || 0})</h3>
                  {purchaseData.league_purchases?.length > 0 ? (
                    <div className="space-y-2">
                      {purchaseData.league_purchases.map((p, i) => (
                        <div key={i} className="bg-slate-900/60 rounded-lg p-3 flex items-center justify-between" data-testid={`slot-purchase-${i}`}>
                          <div>
                            <p className="text-white text-sm font-medium">{p.product_id}</p>
                            <p className="text-slate-500 text-xs">
                              {p.purchased_at ? new Date(p.purchased_at).toLocaleString() : "N/A"}
                              {p.source === "admin_grant" && <span className="ml-1 text-amber-400">(Admin Grant)</span>}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No slot purchases</p>
                  )}
                  {/* Admin-granted extra slots */}
                  {(purchaseData.extra_league_slots?.private > 0 || purchaseData.extra_league_slots?.public > 0) && (
                    <div className="mt-2 bg-slate-900/40 rounded-lg p-2 text-xs text-slate-400">
                      Admin-granted extra slots: Private: <span className="text-white">{purchaseData.extra_league_slots.private || 0}</span> | Public: <span className="text-white">{purchaseData.extra_league_slots.public || 0}</span>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="border-t border-slate-700"></div>

                {/* Grant League Size Upgrade */}
                <div>
                  <h3 className="text-sm font-semibold text-emerald-400 mb-3 uppercase tracking-wider">Grant League Size Upgrade</h3>
                  <div className="flex flex-wrap gap-2 items-end">
                    <div className="flex-1 min-w-[140px]">
                      <label className="text-slate-400 text-xs mb-1 block">League</label>
                      <select
                        value={grantSizeLeague}
                        onChange={(e) => setGrantSizeLeague(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-emerald-500 focus:outline-none"
                        data-testid="grant-size-league-select"
                      >
                        <option value="">Select league...</option>
                        {purchaseData.leagues?.map((lg) => (
                          <option key={lg.id} value={lg.id}>
                            {lg.name} ({lg.member_count}/{lg.max_members}) {lg.is_owner ? "[Owner]" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-24">
                      <label className="text-slate-400 text-xs mb-1 block">Size</label>
                      <select
                        value={grantSizeValue}
                        onChange={(e) => setGrantSizeValue(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-emerald-500 focus:outline-none"
                        data-testid="grant-size-value-select"
                      >
                        <option value="15">15</option>
                        <option value="30">30</option>
                        <option value="50">50</option>
                      </select>
                    </div>
                    <button
                      onClick={handleGrantSize}
                      disabled={granting || !grantSizeLeague}
                      className="px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1"
                      data-testid="grant-size-btn"
                    >
                      {granting ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
                      Grant Size
                    </button>
                  </div>
                </div>

                {/* Grant League Slot */}
                <div>
                  <h3 className="text-sm font-semibold text-purple-400 mb-3 uppercase tracking-wider">Grant League Slot</h3>
                  <div className="flex gap-2 items-end">
                    <div className="w-36">
                      <label className="text-slate-400 text-xs mb-1 block">Slot Type</label>
                      <select
                        value={grantSlotType}
                        onChange={(e) => setGrantSlotType(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                        data-testid="grant-slot-type-select"
                      >
                        <option value="private">Private</option>
                        <option value="public">Public</option>
                      </select>
                    </div>
                    <button
                      onClick={handleGrantSlot}
                      disabled={granting}
                      className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1"
                      data-testid="grant-slot-btn"
                    >
                      {granting ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
                      Grant Slot
                    </button>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-slate-700"></div>

                {/* Grant Budget Bonus */}
                <div>
                  <h3 className="text-sm font-semibold text-amber-400 mb-3 uppercase tracking-wider">
                    Grant Budget Bonus
                  </h3>
                  <p className="text-slate-500 text-xs mb-3">
                    Current bonus: <span className="text-white font-medium">
                      +{purchaseModalUser.admin_budget_bonus || 0} Cr
                    </span>
                  </p>
                  <div className="flex gap-2 items-end">
                    <div className="w-28">
                      <label className="text-slate-400 text-xs mb-1 block">Bonus Amount</label>
                      <select
                        value={grantBonusAmount}
                        onChange={(e) => setGrantBonusAmount(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none"
                        data-testid="grant-bonus-amount-select"
                      >
                        <option value="1">+1 Cr</option>
                        <option value="2">+2 Cr</option>
                        <option value="5">+5 Cr</option>
                      </select>
                    </div>
                    <button
                      onClick={handleGrantBudgetBonus}
                      disabled={grantingBonus}
                      className="px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1"
                      data-testid="grant-bonus-btn"
                    >
                      {grantingBonus
                        ? <Loader2 className="animate-spin" size={14} />
                        : <Plus size={14} />}
                      Grant Bonus
                    </button>
                    <button
                      onClick={handleClearBudgetBonus}
                      disabled={grantingBonus || !purchaseModalUser.admin_budget_bonus}
                      className="px-4 py-2 bg-slate-700/50 text-slate-400 rounded-lg hover:bg-slate-600/50 transition-colors text-sm font-medium disabled:opacity-50"
                      data-testid="clear-bonus-btn"
                    >
                      Clear
                    </button>
                  </div>
                  {bonusGrantSuccess && (
                    <p className="text-emerald-400 text-xs mt-2" data-testid="bonus-grant-success">{bonusGrantSuccess}</p>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Data Sync</h3>
          <p className="text-slate-400 text-sm mb-4">
            Sync match schedules and player squads from Cricbuzz API.
          </p>
          <button 
            onClick={async () => {
              try {
                setSuccess("Syncing matches...");
                await axios.post(`${API}/matches/sync`, {}, { headers: getAuthHeader() });
                setSuccess("Matches synced!");
                fetchData();
              } catch (e) {
                setError("Sync failed");
              }
            }}
            className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-all"
          >
            Sync Matches
          </button>
          <button
            data-testid="refresh-tbc-matches-btn"
            onClick={async () => {
              try {
                setSuccess("Refreshing TBC matches from Cricbuzz...");
                const res = await axios.post(`${API}/admin/refresh-matches`, {}, { headers: getAuthHeader() });
                const data = res.data;
                if (data.updated > 0) {
                  setSuccess(`Updated ${data.updated} matches! ${data.remaining_tbc} still TBC.`);
                } else {
                  setSuccess(data.errors?.length ? `No updates. ${data.errors.join(', ')}` : 'No TBC matches to refresh.');
                }
                fetchData();
              } catch (e) {
                setError(e.response?.data?.detail || "Refresh failed");
              }
            }}
            className="px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-all"
          >
            Refresh TBC Matches
          </button>
        </div>
        
        {/* TBC Match Editor */}
        {tbcMatches.length > 0 && (
          <div className="mt-4 p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
            <h4 className="text-amber-400 font-semibold text-sm mb-3">TBC Matches ({tbcMatches.length}) — Set Teams Manually</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {tbcMatches.map(m => (
                <div key={m.match_id} className="flex items-center gap-2 text-sm" data-testid={`tbc-match-${m.match_id}`}>
                  <span className="text-slate-400 w-8 text-right shrink-0">#{m.match_number}</span>
                  <span className="text-slate-500 text-xs w-28 shrink-0 truncate">{m.match_type}</span>
                  <input
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-xs w-32"
                    placeholder="Team 1"
                    defaultValue={m.team1 === "TBC" ? "" : m.team1}
                    id={`tbc-t1-${m.match_id}`}
                  />
                  <span className="text-slate-500 text-xs">vs</span>
                  <input
                    className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-xs w-32"
                    placeholder="Team 2"
                    defaultValue={m.team2 === "TBC" ? "" : m.team2}
                    id={`tbc-t2-${m.match_id}`}
                  />
                  <button
                    onClick={async () => {
                      const t1 = document.getElementById(`tbc-t1-${m.match_id}`).value.trim();
                      const t2 = document.getElementById(`tbc-t2-${m.match_id}`).value.trim();
                      if (!t1 || !t2) { setError("Both team names required"); return; }
                      try {
                        await axios.put(`${API}/admin/matches/${m.match_id}/teams?team1=${encodeURIComponent(t1)}&team2=${encodeURIComponent(t2)}`, {}, { headers: getAuthHeader() });
                        setSuccess(`Match #${m.match_number}: ${t1} vs ${t2} saved!`);
                        fetchData();
                      } catch (e) {
                        setError(e.response?.data?.detail || "Update failed");
                      }
                    }}
                    className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30 text-xs shrink-0"
                    data-testid={`save-tbc-${m.match_id}`}
                  >
                    Save
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Points System</h3>
          <p className="text-slate-400 text-sm mb-4">
            View the fantasy points configuration.
          </p>
          <Link 
            to="/points"
            className="inline-block px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-all"
          >
            View Points System
          </Link>
        </div>
      </div>

      {/* All Leagues Section */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Shield className="text-indigo-400" size={24} />
            <h2 className="text-xl font-semibold text-white">All Leagues</h2>
          </div>
          <button
            onClick={() => { setShowLeaguesSection(!showLeaguesSection); if (!showLeaguesSection) fetchAllLeagues(); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition-all"
            data-testid="toggle-leagues-btn"
          >
            {showLeaguesSection ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            {showLeaguesSection ? "Hide" : "View All"}
          </button>
        </div>
        
        {showLeaguesSection && (
          <div className="space-y-3">
            {allLeagues.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No leagues found</p>
            ) : (
              allLeagues.map(league => (
                <div key={league.id} className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-semibold">{league.name}</h3>
                    <span className="text-xs text-slate-400">Code: <span className="text-emerald-400 font-mono">{league.code}</span></span>
                  </div>
                  <div className="text-xs text-slate-400 mb-2">
                    Created by: <span className="text-white">{league.creator_name}</span> | 
                    Members: <span className="text-white">{(league.members || []).length}</span>
                  </div>
                  {league.member_details && league.member_details.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-700/50">
                      <div className="space-y-1">
                        {league.member_details.map(m => (
                          <div key={m.id} className="flex items-center justify-between text-xs">
                            <span className="text-slate-300">{m.display_name}</span>
                            <span className="text-emerald-400">{m.total_points || 0} pts</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create Transfer Window Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Create Transfer Window</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={createWindow} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Window Name *</label>
                <input
                  type="text"
                  value={newWindow.name}
                  onChange={(e) => setNewWindow({ ...newWindow, name: e.target.value })}
                  placeholder="e.g., Pre-Tournament Window"
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                  data-testid="window-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Start Date *</label>
                  <input
                    type="date"
                    value={newWindow.start_date}
                    onChange={(e) => setNewWindow({ ...newWindow, start_date: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    data-testid="window-start-date"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">End Date *</label>
                  <input
                    type="date"
                    value={newWindow.end_date}
                    onChange={(e) => setNewWindow({ ...newWindow, end_date: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    data-testid="window-end-date"
                  />
                </div>
              </div>
              
              {/* New Users Only Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-900/50 border border-slate-700 rounded-lg">
                <div>
                  <p className="text-white font-medium text-sm">New Users Only (Mid-Week Transfer)</p>
                  <p className="text-slate-400 text-xs mt-1">Only users without a team can use this window. They won't get points for already synced matches.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setNewWindow({ ...newWindow, new_users_only: !newWindow.new_users_only })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${newWindow.new_users_only ? 'bg-amber-500' : 'bg-slate-600'}`}
                  data-testid="new-users-only-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${newWindow.new_users_only ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              
              {/* Finals Mode Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-900/50 border border-slate-700 rounded-lg">
                <div>
                  <p className="text-white font-medium text-sm">Finals Mode</p>
                  <p className="text-slate-400 text-xs mt-1">No per-team cap. Players can be from only 2 teams (11 players total).</p>
                </div>
                <button
                  type="button"
                  onClick={() => setNewWindow({ ...newWindow, finals_mode: !newWindow.finals_mode })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${newWindow.finals_mode ? 'bg-red-500' : 'bg-slate-600'}`}
                  data-testid="finals-mode-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${newWindow.finals_mode ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              
              <button
                className="w-full bg-emerald-500 text-white font-semibold py-3 rounded-lg hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="create-window-submit"
              >
                {saving ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Create Window
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Feedback Dashboard */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden" data-testid="feedback-dashboard">
        <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-800/70 transition-colors"
          onClick={() => { setShowFeedbackSection(!showFeedbackSection); if (!showFeedbackSection && feedbackData.total === 0) fetchFeedbackData(); }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <Star className="text-cyan-400" size={20} />
            </div>
            <div>
              <h3 className="text-white font-semibold">Feedback Dashboard</h3>
              <p className="text-slate-400 text-xs">{feedbackData.total} responses</p>
            </div>
          </div>
          <button className="flex items-center gap-1 text-sm text-cyan-400">
            {showFeedbackSection ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            {showFeedbackSection ? "Hide" : "View"}
          </button>
        </div>

        {/* ========== FEEDBACK SECTION ========== */}
        {showFeedbackSection && (
          <div className="border-t border-slate-700 p-4 space-y-4">
            {feedbackLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="animate-spin text-cyan-400" size={24} /></div>
            ) : feedbackData.total === 0 ? (
              <p className="text-slate-400 text-sm text-center py-4">No feedback yet</p>
            ) : (
              <>
                {/* Analytics Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-400">{feedbackData.analytics?.avg_rating || 0}</p>
                    <p className="text-xs text-slate-400">Avg Rating</p>
                  </div>
                  <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-emerald-400">{feedbackData.total}</p>
                    <p className="text-xs text-slate-400">Responses</p>
                  </div>
                  <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-cyan-400">{feedbackData.analytics?.total_users || 0}</p>
                    <p className="text-xs text-slate-400">Total Users</p>
                  </div>
                  <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-400">{feedbackData.analytics?.submission_rate || 0}%</p>
                    <p className="text-xs text-slate-400">Response Rate</p>
                  </div>
                </div>

                {/* Rating Distribution */}
                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3">
                  <h4 className="text-white font-medium text-sm mb-2">Rating Distribution</h4>
                  {[5, 4, 3, 2, 1].map(r => {
                    const count = feedbackData.analytics?.rating_distribution?.[r] || 0;
                    const pct = feedbackData.total > 0 ? (count / feedbackData.total * 100) : 0;
                    return (
                      <div key={r} className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-amber-400 w-8">{r} star</span>
                        <div className="flex-1 bg-slate-800 rounded-full h-3">
                          <div className="bg-amber-500/60 h-3 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-slate-400 w-6 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Feature Insights */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Best Features */}
                  <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3">
                    <h4 className="text-emerald-400 font-medium text-xs mb-2">Best Features</h4>
                    {Object.entries(feedbackData.analytics?.best_features || {}).slice(0, 5).map(([f, c]) => (
                      <div key={f} className="flex justify-between text-xs py-0.5">
                        <span className="text-slate-300 truncate">{f}</span>
                        <span className="text-emerald-400 font-mono ml-2">{c}</span>
                      </div>
                    ))}
                    {Object.keys(feedbackData.analytics?.best_features || {}).length === 0 && <p className="text-xs text-slate-500">None yet</p>}
                  </div>
                  {/* Needs Improvement */}
                  <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3">
                    <h4 className="text-red-400 font-medium text-xs mb-2">Needs Improvement</h4>
                    {Object.entries(feedbackData.analytics?.needs_improvement || {}).slice(0, 5).map(([f, c]) => (
                      <div key={f} className="flex justify-between text-xs py-0.5">
                        <span className="text-slate-300 truncate">{f}</span>
                        <span className="text-red-400 font-mono ml-2">{c}</span>
                      </div>
                    ))}
                    {Object.keys(feedbackData.analytics?.needs_improvement || {}).length === 0 && <p className="text-xs text-slate-500">None yet</p>}
                  </div>
                  {/* Didn't Use */}
                  <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3">
                    <h4 className="text-slate-400 font-medium text-xs mb-2">Not Used</h4>
                    {Object.entries(feedbackData.analytics?.didnt_use || {}).slice(0, 5).map(([f, c]) => (
                      <div key={f} className="flex justify-between text-xs py-0.5">
                        <span className="text-slate-300 truncate">{f}</span>
                        <span className="text-slate-400 font-mono ml-2">{c}</span>
                      </div>
                    ))}
                    {Object.keys(feedbackData.analytics?.didnt_use || {}).length === 0 && <p className="text-xs text-slate-500">None yet</p>}
                  </div>
                </div>

                {/* Wishlist Keywords */}
                {Object.keys(feedbackData.analytics?.wishlist_keywords || {}).length > 0 && (
                  <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3">
                    <h4 className="text-purple-400 font-medium text-xs mb-2">Most Requested Features (from wishlist text)</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(feedbackData.analytics?.wishlist_keywords || {}).map(([term, c]) => (
                        <span key={term} className="px-2 py-1 rounded-md bg-purple-500/10 border border-purple-500/30 text-purple-400 text-xs">
                          {term} ({c})
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Individual Responses */}
                <div className="space-y-2">
                  <h4 className="text-white font-medium text-sm">Individual Responses ({feedbackData.feedbacks.length})</h4>
                  {feedbackData.feedbacks.map((fb) => (
                    <div key={fb.id} className={`bg-slate-900/50 border rounded-lg p-3 ${
                      fb.type === "simple" && fb.category === "bug_report" ? "border-red-500/30" :
                      fb.type === "simple" && fb.category === "feature_request" ? "border-amber-500/30" :
                      "border-slate-700"
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-white text-sm font-medium">{fb.display_name || 'Unknown'}</p>
                          <p className="text-slate-500 text-xs">{fb.email}</p>
                        </div>
                        {fb.type === "simple" ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            fb.category === "bug_report" ? "bg-red-500/20 text-red-400" :
                            fb.category === "feature_request" ? "bg-amber-500/20 text-amber-400" :
                            "bg-emerald-500/20 text-emerald-400"
                          }`}>
                            {fb.category === "bug_report" ? "Bug Report" : 
                             fb.category === "feature_request" ? "Feature Request" : "General"}
                          </span>
                        ) : (
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} size={12} className={i < fb.overall_rating ? "text-amber-400 fill-amber-400" : "text-slate-600"} />
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Simple feedback format */}
                      {fb.type === "simple" && fb.message && (
                        <div className="text-sm text-slate-300 whitespace-pre-wrap bg-slate-800/50 rounded p-2 text-xs">
                          {fb.message}
                        </div>
                      )}
                      {/* Old survey-style feedback format */}
                      {fb.type !== "simple" && (
                        <>
                          {fb.best_features?.length > 0 && (
                            <p className="text-xs text-slate-400 mb-1">Best: <span className="text-emerald-400">{fb.best_features.join(", ")}{fb.best_other ? `, ${fb.best_other}` : ''}</span></p>
                          )}
                          {fb.needs_improvement?.length > 0 && (
                            <div className="mb-1">
                              <p className="text-xs text-slate-400">Improve: <span className="text-red-400">{fb.needs_improvement.join(", ")}{fb.improvement_other ? `, ${fb.improvement_other}` : ''}</span></p>
                              {fb.improvement_details && Object.entries(fb.improvement_details).filter(([, v]) => v).map(([k, v]) => (
                                <p key={k} className="text-xs text-slate-500 ml-2">- {k}: {v}</p>
                              ))}
                            </div>
                          )}
                          {fb.didnt_use?.length > 0 && (
                            <p className="text-xs text-slate-400 mb-1">Didn't use: <span className="text-slate-500">{fb.didnt_use.join(", ")}{fb.didnt_use_other ? `, ${fb.didnt_use_other}` : ''}</span></p>
                          )}
                          {fb.feature_wishlist && <p className="text-xs text-slate-400 mb-1">Wishlist: <span className="text-purple-400">{fb.feature_wishlist}</span></p>}
                          {fb.additional_comments && <p className="text-xs text-slate-400">Comments: <span className="text-white">{fb.additional_comments}</span></p>}
                        </>
                      )}
                      <p className="text-xs text-slate-600 mt-1">{new Date(fb.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ========== TIER MANAGEMENT SECTION ========== */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden" data-testid="tier-management-section">
          <button onClick={() => setShowTierSection(!showTierSection)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/30 transition-all">
            <div className="flex items-center gap-2">
              <Star size={18} className="text-amber-400" />
              <span className="text-white font-medium text-sm">Tier Management</span>
            </div>
            {showTierSection ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showTierSection && (
            <div className="px-4 pb-4 space-y-3">
              <div className="flex gap-2">
                <input
                  type="text" value={tierSearch} onChange={(e) => setTierSearch(e.target.value)}
                  placeholder="Search user email or name..."
                  className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-amber-500 focus:outline-none"
                  data-testid="tier-search-input"
                />
                <button
                  onClick={async () => {
                    setTierLoading(true);
                    try {
                      const headers = await getAuthHeader();
                      const res = await axios.get(`${API}/admin/users/list?search=${encodeURIComponent(tierSearch)}&limit=20`, { headers });
                      setTierUsers(res.data.users || []);
                    } catch (e) { setError("Failed to load users"); } finally { setTierLoading(false); }
                  }}
                  className="px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg text-sm font-medium hover:bg-amber-500/30 transition-colors"
                  data-testid="tier-search-btn"
                >
                  {tierLoading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
                </button>
              </div>
              {tierUsers.map(u => (
                <div key={u.id} className="bg-slate-900/50 rounded-lg p-3" data-testid={`tier-user-${u.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-white text-sm font-medium flex items-center gap-2">
                        {u.display_name || u.email}
                        {u.display_name && u.display_name.includes("@") && (
                          <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded font-medium" title="User has email as display name - needs to update">
                            @
                          </span>
                        )}
                      </p>
                      <p className="text-slate-500 text-xs">{u.email} • {u.total_points || 0} pts{u.is_tester ? ' • Tester' : ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        defaultValue={u.tier || "free"}
                        onChange={async (e) => {
                          try {
                            const headers = await getAuthHeader();
                            const expiresAt = e.target.value === "free" ? null : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
                            await axios.put(`${API}/admin/users/${u.id}/tier`, { tier: e.target.value, expires_at: expiresAt }, { headers });
                            setSuccess(`Tier updated to ${e.target.value} for ${u.display_name || u.email}`);
                            setTimeout(() => setSuccess(""), 3000);
                          } catch (err) { setError("Failed to update tier"); }
                        }}
                        className="px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-xs"
                        data-testid={`tier-select-${u.id}`}
                      >
                        <option value="free">Free</option>
                        <option value="pro">Pro</option>
                        <option value="pro_plus">Pro Plus</option>
                        <option value="pro_max">Pro Max</option>
                      </select>
                    </div>
                  </div>
                  {/* Consumable Usage Info */}
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-1 bg-slate-800 rounded text-slate-400">
                      Predictions: <span className="text-white">{u.predictions_made || 0}</span>
                    </span>
                    <span className="px-2 py-1 bg-slate-800 rounded text-slate-400">
                      Transfers: <span className="text-white">{u.transfers_used || 0}</span>
                    </span>
                    <span className="px-2 py-1 bg-slate-800 rounded text-slate-400">
                      Unlocks: <span className="text-white">{u.unlocked_count || 0}</span>
                    </span>
                    {u.tier_expires_at && (
                      <span className="px-2 py-1 bg-amber-500/20 rounded text-amber-400">
                        Expires: {new Date(u.tier_expires_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  {/* Grant League Slot Button */}
                  <div className="flex gap-1 mt-2">
                    <button
                      onClick={async () => {
                        try {
                          const headers = await getAuthHeader();
                          const newVal = !u.is_tester;
                          await axios.put(`${API}/admin/users/${u.id}/tester`, { is_tester: newVal }, { headers });
                          setSuccess(`${u.display_name || u.email} is ${newVal ? 'now' : 'no longer'} a tester`);
                          u.is_tester = newVal;
                          setTierUsers([...tierUsers]);
                          setTimeout(() => setSuccess(""), 3000);
                        } catch (e) {
                          setError("Failed to toggle tester status");
                        }
                      }}
                      className={`px-2 py-1 rounded text-xs transition-colors ${
                        u.is_tester
                          ? "bg-cyan-500/30 text-cyan-300 hover:bg-cyan-500/40 border border-cyan-500/40"
                          : "bg-slate-700/50 text-slate-400 hover:bg-slate-700/80"
                      }`}
                      data-testid={`toggle-tester-${u.id}`}
                    >
                      {u.is_tester ? "Tester ON" : "Tester OFF"}
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const headers = await getAuthHeader();
                          const res = await axios.post(`${API}/admin/users/${u.id}/grant-league-slot`, { type: "private" }, { headers });
                          setSuccess(res.data?.message || `Granted 1 private league slot to ${u.display_name || u.email}`);
                          setTimeout(() => setSuccess(""), 3000);
                        } catch (e) { 
                          console.error("Grant slot error:", e);
                          setError(e.response?.data?.detail || "Failed to grant slot"); 
                        }
                      }}
                      className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs hover:bg-purple-500/30 transition-colors"
                      data-testid={`grant-private-slot-${u.id}`}
                    >
                      +Private Slot
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const headers = await getAuthHeader();
                          const res = await axios.post(`${API}/admin/users/${u.id}/grant-league-slot`, { type: "public" }, { headers });
                          setSuccess(res.data?.message || `Granted 1 public league slot to ${u.display_name || u.email}`);
                          setTimeout(() => setSuccess(""), 3000);
                        } catch (e) { 
                          console.error("Grant slot error:", e);
                          setError(e.response?.data?.detail || "Failed to grant slot"); 
                        }
                      }}
                      className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs hover:bg-emerald-500/30 transition-colors"
                      data-testid={`grant-public-slot-${u.id}`}
                    >
                      +Public Slot
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      {/* ========== LEAGUE ADMIN SECTION ========== */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden" data-testid="league-admin-section">
          <button onClick={() => setShowLeagueAdminSection(!showLeagueAdminSection)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/30 transition-all">
            <div className="flex items-center gap-2">
              <Users size={18} className="text-purple-400" />
              <span className="text-white font-medium text-sm">League Admin</span>
            </div>
            {showLeagueAdminSection ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showLeagueAdminSection && (
            <div className="px-4 pb-4 space-y-3">
              <p className="text-slate-400 text-xs">Search for a user's leagues and grant member capacity upgrades</p>
              
              {/* Search User */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={leagueAdminSearchEmail}
                  onChange={(e) => setLeagueAdminSearchEmail(e.target.value)}
                  placeholder="Enter user email..."
                  className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-purple-500"
                />
                <button
                  onClick={async () => {
                    if (!leagueAdminSearchEmail) return;
                    setLeagueAdminLoading(true);
                    try {
                      const headers = await getAuthHeader();
                      const res = await axios.get(`${API}/admin/leagues/by-user?email=${encodeURIComponent(leagueAdminSearchEmail)}`, { headers });
                      setLeagueAdminResults(res.data);
                    } catch (e) {
                      setError(e.response?.data?.detail || "Failed to fetch user leagues");
                      setLeagueAdminResults(null);
                    } finally {
                      setLeagueAdminLoading(false);
                    }
                  }}
                  disabled={leagueAdminLoading}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition-colors disabled:opacity-50"
                >
                  {leagueAdminLoading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
                </button>
              </div>
              
              {/* Results */}
              {leagueAdminResults && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
                    <div>
                      <p className="text-white font-medium">{leagueAdminResults.user?.display_name || leagueAdminResults.user?.email}</p>
                      <p className="text-slate-500 text-xs">{leagueAdminResults.user?.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-400 text-sm">{leagueAdminResults.leagues?.length || 0} leagues</p>
                      <p className="text-slate-500 text-xs">Slots: {leagueAdminResults.slots?.create_used || 0}/{leagueAdminResults.slots?.create_available || 1}</p>
                    </div>
                  </div>
                  
                  {leagueAdminResults.leagues?.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {leagueAdminResults.leagues.map(league => (
                        <div key={league.id} className="bg-slate-900/30 border border-slate-700 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="text-white font-medium text-sm flex items-center gap-2">
                                {league.visibility === "public" ? <Globe size={12} className="text-emerald-400" /> : <Lock size={12} className="text-purple-400" />}
                                {league.name}
                              </p>
                              <p className="text-slate-500 text-xs">Members: {league.member_count}/{league.max_members}</p>
                            </div>
                          </div>
                          {/* Grant Capacity Buttons */}
                          <div className="flex gap-2 mt-2">
                            {[15, 30, 50].filter(size => size > (league.max_members || 5)).map(size => (
                              <button
                                key={size}
                                onClick={async () => {
                                  try {
                                    const headers = await getAuthHeader();
                                    await axios.post(`${API}/admin/leagues/${league.id}/set-capacity`, { max_members: size }, { headers });
                                    setSuccess(`Set ${league.name} capacity to ${size} members`);
                                    // Refresh results
                                    const res = await axios.get(`${API}/admin/leagues/by-user?email=${encodeURIComponent(leagueAdminSearchEmail)}`, { headers });
                                    setLeagueAdminResults(res.data);
                                    setTimeout(() => setSuccess(""), 3000);
                                  } catch (e) {
                                    setError(e.response?.data?.detail || "Failed to update capacity");
                                  }
                                }}
                                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                  size === 15 ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30" :
                                  size === 30 ? "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" :
                                  "bg-purple-500/20 text-purple-400 hover:bg-purple-500/30"
                                }`}
                              >
                                Set {size}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm text-center py-4">No leagues found for this user</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

      {/* ========== SPONSOR MANAGEMENT SECTION ========== */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden" data-testid="sponsor-management-section">
          <button onClick={() => setShowSponsorSection(!showSponsorSection)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/30 transition-all">
            <div className="flex items-center gap-2">
              <Share2 size={18} className="text-blue-400" />
              <span className="text-white font-medium text-sm">Match Sponsors</span>
            </div>
            {showSponsorSection ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showSponsorSection && (
            <div className="px-4 pb-4 space-y-3">
              <input type="text" value={sponsorMatchId} onChange={(e) => setSponsorMatchId(e.target.value)} placeholder="Match ID" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-blue-500 focus:outline-none" data-testid="sponsor-match-id" />
              <input type="text" value={sponsorName} onChange={(e) => setSponsorName(e.target.value)} placeholder="Sponsor Name" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-blue-500 focus:outline-none" data-testid="sponsor-name" />
              <input type="text" value={sponsorLogoUrl} onChange={(e) => setSponsorLogoUrl(e.target.value)} placeholder="Logo URL (optional)" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-blue-500 focus:outline-none" data-testid="sponsor-logo-url" />
              <input type="text" value={sponsorLink} onChange={(e) => setSponsorLink(e.target.value)} placeholder="Sponsor Link (optional)" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-blue-500 focus:outline-none" data-testid="sponsor-link" />
              <textarea 
                value={sponsorDescription} 
                onChange={(e) => setSponsorDescription(e.target.value.slice(0, 200))} 
                placeholder="Brief description (max 2 lines, 200 chars)"
                rows={2}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-blue-500 focus:outline-none resize-none"
                data-testid="sponsor-description"
              />
              <p className="text-xs text-slate-500">{sponsorDescription.length}/200 characters</p>
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    if (!sponsorMatchId || !sponsorName) return;
                    try {
                      const headers = await getAuthHeader();
                      await axios.put(`${API}/admin/matches/${sponsorMatchId}/sponsor`, { name: sponsorName, logo_url: sponsorLogoUrl || null, link: sponsorLink || null, description: sponsorDescription || null }, { headers });
                      setSuccess("Sponsor assigned"); setSponsorMatchId(""); setSponsorName(""); setSponsorLogoUrl(""); setSponsorLink(""); setSponsorDescription("");
                      setTimeout(() => setSuccess(""), 3000);
                    } catch (e) { setError("Failed to set sponsor"); }
                  }}
                  className="flex-1 py-2 bg-blue-500/20 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/30 transition-colors"
                  data-testid="sponsor-set-btn"
                >
                  Set Sponsor
                </button>
                <button
                  onClick={async () => {
                    if (!sponsorMatchId) return;
                    try {
                      const headers = await getAuthHeader();
                      await axios.delete(`${API}/admin/matches/${sponsorMatchId}/sponsor`, { headers });
                      setSuccess("Sponsor removed"); setTimeout(() => setSuccess(""), 3000);
                    } catch (e) { setError("Failed to remove sponsor"); }
                  }}
                  className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-colors"
                  data-testid="sponsor-remove-btn"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
        </div>

      {/* ========== PUSH BROADCAST SECTION ========== */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden" data-testid="push-broadcast-section">
          <button onClick={() => setShowPushSection(!showPushSection)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/30 transition-all">
            <div className="flex items-center gap-2">
              <Zap size={18} className="text-purple-400" />
              <span className="text-white font-medium text-sm">Push Notifications</span>
            </div>
            {showPushSection ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showPushSection && (
            <div className="px-4 pb-4 space-y-3">
              <input type="text" value={pushTitle} onChange={(e) => setPushTitle(e.target.value)} placeholder="Notification Title" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-purple-500 focus:outline-none" data-testid="push-title" />
              <textarea value={pushBody} onChange={(e) => setPushBody(e.target.value)} placeholder="Notification Body" rows={3} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-purple-500 focus:outline-none resize-none" data-testid="push-body" />
              <select value={pushTarget} onChange={(e) => setPushTarget(e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm" data-testid="push-target">
                <option value="all">All Users</option>
                <option value="pro">Pro Users Only</option>
                <option value="free">Free Users Only</option>
              </select>
              <input
                type="text"
                placeholder="Deep link path — optional, e.g. /leaderboard or /my-team"
                value={pushDeepLink}
                onChange={e => setPushDeepLink(e.target.value)}
                className="w-full bg-slate-900 text-white rounded-lg px-3 py-2 text-sm border border-slate-700 focus:border-emerald-500 outline-none"
                data-testid="push-deep-link"
              />
              <button
                onClick={async () => {
                  if (!pushTitle || !pushBody) return;
                  setPushSending(true);
                  try {
                    const headers = await getAuthHeader();
                    const res = await axios.post(`${API}/admin/push/broadcast`, { title: pushTitle, body: pushBody, target: pushTarget, deep_link: pushDeepLink || null }, { headers });
                    setLastBroadcastResult(res.data);
                    setSuccess(`Push sent: ${res.data.sent} delivered, ${res.data.failed} failed`);
                    setPushTitle(""); setPushBody("");
                    setTimeout(() => setSuccess(""), 5000);
                  } catch (e) { setError("Failed to send push"); } finally { setPushSending(false); }
                }}
                disabled={pushSending || !pushTitle || !pushBody}
                className="w-full py-2 bg-purple-500/20 text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="push-send-btn"
              >
                {pushSending ? <><Loader2 className="animate-spin" size={16} /> Sending...</> : "Send Broadcast"}
              </button>

              {lastBroadcastResult && (
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3" data-testid="broadcast-result-card">
                  <h3 className="text-white font-medium">Broadcast Result</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-slate-400 text-xs uppercase">Sent</div>
                      <div className="text-emerald-400 font-bold text-xl">{lastBroadcastResult.sent ?? 0}</div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-xs uppercase">Failed</div>
                      <div className="text-red-400 font-bold text-xl">{lastBroadcastResult.failed ?? 0}</div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-xs uppercase">Total tokens</div>
                      <div className="text-slate-300 font-bold text-xl">{lastBroadcastResult.total_tokens ?? 0}</div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-xs uppercase">Stale purged</div>
                      <div className="text-emerald-400 font-bold text-xl">{lastBroadcastResult.stale_tokens_purged ?? 0}</div>
                    </div>
                  </div>
                  {lastBroadcastResult.error_breakdown && Object.keys(lastBroadcastResult.error_breakdown).length > 0 && (
                    <div className="pt-3 border-t border-slate-700">
                      <div className="text-slate-400 text-xs uppercase mb-2">Error breakdown</div>
                      <div className="space-y-1">
                        {Object.entries(lastBroadcastResult.error_breakdown).map(([code, count]) => (
                          <div key={code} className="flex justify-between text-sm">
                            <span className="text-amber-400 font-mono">{code}</span>
                            <span className="text-slate-300">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {lastBroadcastResult.stale_tokens_purged > 0 && (
                    <p className="text-slate-500 text-xs italic pt-2">
                      Dead tokens removed. Next broadcast will only reach active users.
                    </p>
                  )}
                </div>
              )}
              <button
                onClick={async () => {
                  try {
                    const headers = await getAuthHeader();
                    const res = await axios.post(`${API}/admin/cron/notifications`, { trigger: "all" }, { headers });
                    setSuccess(`Cron run: ${JSON.stringify(res.data.results)}`);
                    setTimeout(() => setSuccess(""), 5000);
                  } catch (e) { setError("Cron failed"); }
                }}
                className="w-full py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600 transition-colors"
                data-testid="push-cron-btn"
              >
                Run Auto-Notifications (Cron)
              </button>

              {/* One-shot: Backfill pre-sync rank snapshots for historical matches */}
              <div className="pt-2 border-t border-slate-700/50">
                <button
                  onClick={async () => {
                    if (!window.confirm(
                      "This will backfill rank snapshots for all historical matches missing them. Proceed?"
                    )) return;
                    setBackfillingRanks(true);
                    setError("");
                    try {
                      const headers = await getAuthHeader();
                      const res = await axios.post(
                        `${API}/admin/backfill-pre-sync-ranks`,
                        {},
                        { headers }
                      );
                      const { backfilled_weekly = 0, backfilled_daily = 0 } = res.data || {};
                      setSuccess(
                        `Backfill complete: ${backfilled_weekly} weekly + ${backfilled_daily} daily matches updated`
                      );
                      setTimeout(() => setSuccess(""), 8000);
                    } catch (e) {
                      setError(e.response?.data?.detail || "Backfill failed");
                    } finally {
                      setBackfillingRanks(false);
                    }
                  }}
                  disabled={backfillingRanks}
                  className="w-full py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm font-medium hover:bg-cyan-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="backfill-pre-sync-ranks-btn"
                >
                  {backfillingRanks ? (
                    <><Loader2 className="animate-spin" size={16} /> Backfilling (may take 10-30s)...</>
                  ) : (
                    "Backfill Pre-Sync Ranks"
                  )}
                </button>
                <p className="text-slate-500 text-xs mt-1.5 leading-relaxed">
                  Populates rank snapshots for historical matches. One-time use.
                  Safe to run multiple times (idempotent — only writes to matches missing the snapshots).
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={async () => {
                    try {
                      const headers = await getAuthHeader();
                      const preview = await axios.post(
                        `${API}/admin/email/onboard-no-team?dry_run=true`,
                        {},
                        { headers }
                      );
                      const names = preview.data.users
                        .map(u => `${u.name} (${u.email})`)
                        .join("\n");
                      const confirmed = window.confirm(
                        `Will send to ${preview.data.would_send_to} users:\n\n${names}\n\nSend now?`
                      );
                      if (confirmed) {
                        const res = await axios.post(
                          `${API}/admin/email/onboard-no-team`,
                          {},
                          { headers }
                        );
                        setSuccess(`Sent to ${res.data.sent} users without teams`);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        alert(`Sent onboarding email to ${res.data.sent} users`);
                      }
                    } catch (e) {
                      setError(e.response?.data?.detail || "Failed to send emails");
                    }
                  }}
                  className="px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-all text-sm"
                  data-testid="email-no-team-btn"
                >
                  Email users without teams
                </button>
                <button
                  onClick={async () => {
                    try {
                      const headers = await getAuthHeader();
                      const res = await axios.post(
                        `${API}/admin/cron/notifications`,
                        { trigger: "match_reminder" },
                        { headers }
                      );
                      const count = res.data.results?.match_reminder ?? 0;
                      setSuccess(`Match reminders sent to ${count} users`);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      alert(`Match reminders sent to ${count} users`);
                    } catch (e) {
                      const msg = e.response?.data?.detail || "Failed";
                      setError(msg);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      alert(`Error: ${msg}`);
                    }
                  }}
                  className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-all text-sm"
                  data-testid="send-match-reminders-btn"
                >
                  Send match reminders
                </button>
                <button
                  onClick={async () => {
                    try {
                      const headers = await getAuthHeader();
                      const res = await axios.post(
                        `${API}/admin/cron/notifications`,
                        { trigger: "daily_reward" },
                        { headers }
                      );
                      const count = res.data.results?.daily_reward ?? 0;
                      setSuccess(`Daily reward reminders sent to ${count} users`);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      alert(`Daily reward reminders sent to ${count} users`);
                    } catch (e) {
                      const msg = e.response?.data?.detail || "Failed";
                      setError(msg);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      alert(`Error: ${msg}`);
                    }
                  }}
                  className="px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-all text-sm"
                  data-testid="send-daily-reward-btn"
                >
                  Send daily reward reminders
                </button>
              </div>
            </div>
          )}
        </div>

      {/* Tournament Management Section */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden" data-testid="tournament-section">
          <button onClick={() => setShowTournamentSection(!showTournamentSection)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/30 transition-all">
            <div className="flex items-center gap-3">
              <Calendar className="text-purple-400" size={20} />
              <span className="font-semibold text-white">Tournament Management</span>
            </div>
            {showTournamentSection ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showTournamentSection && (
            <div className="p-4 border-t border-slate-700 space-y-4">
              {/* Active Tournament Info */}
              <div className="bg-slate-900/50 rounded-lg p-3">
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Active Tournament</p>
                {activeTournament ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{activeTournament.name}</p>
                      <p className="text-slate-500 text-sm">Series ID: {activeTournament.series_id}</p>
                    </div>
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs font-medium">Active</span>
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No active tournament. Initialize one below.</p>
                )}
              </div>

              {/* Captain & VC change — unlimited override */}
              <div className="bg-slate-900/50 rounded-lg p-3" data-testid="captain-unlimited-section">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-slate-400 text-xs uppercase tracking-wider">
                    Captain &amp; VC change — unlimited until (UTC)
                  </p>
                  {captainUnlimitedActive ? (
                    <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs font-medium" data-testid="captain-unlimited-active-badge">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-slate-700/40 text-slate-400 rounded text-xs font-medium">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-slate-500 text-xs mb-2">
                  While in the future, captain &amp; VC changes bypass the once-per-week limit.
                  Clearing this or setting a past datetime restores the limit.
                </p>
                {captainUnlimitedUntil && (
                  <p className="text-slate-400 text-xs mb-2" data-testid="captain-unlimited-current">
                    Current: <span className="text-white font-mono">{captainUnlimitedUntil}</span>
                  </p>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="datetime-local"
                    value={captainUnlimitedInput}
                    onChange={(e) => setCaptainUnlimitedInput(e.target.value)}
                    className="flex-1 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                    data-testid="captain-unlimited-input"
                  />
                  <button
                    onClick={async () => {
                      if (!captainUnlimitedInput) {
                        setError("Pick a datetime first or click Clear");
                        return;
                      }
                      setCaptainUnlimitedSaving(true);
                      setError("");
                      try {
                        // datetime-local has no timezone — treat as UTC
                        const iso = `${captainUnlimitedInput}:00+00:00`;
                        const headers = await getAuthHeader();
                        const res = await axios.post(`${API}/admin/captain-change/unlimited-until`, { until: iso }, { headers });
                        setCaptainUnlimitedUntil(res.data.captain_change_unlimited_until || "");
                        const refresh = await axios.get(`${API}/admin/captain-change/unlimited-until`, { headers });
                        setCaptainUnlimitedActive(!!refresh.data.active);
                        setSuccess("Captain/VC unlimited window saved");
                        setTimeout(() => setSuccess(""), 4000);
                      } catch (e) {
                        setError(e.response?.data?.detail || "Failed to save");
                      } finally {
                        setCaptainUnlimitedSaving(false);
                      }
                    }}
                    disabled={captainUnlimitedSaving}
                    className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="captain-unlimited-save-btn"
                  >
                    {captainUnlimitedSaving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                    Save
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm("Clear unlimited override? Once-per-week limit will resume immediately.")) return;
                      setCaptainUnlimitedSaving(true);
                      setError("");
                      try {
                        const headers = await getAuthHeader();
                        await axios.post(`${API}/admin/captain-change/unlimited-until`, { until: null }, { headers });
                        setCaptainUnlimitedUntil("");
                        setCaptainUnlimitedInput("");
                        setCaptainUnlimitedActive(false);
                        setSuccess("Captain/VC unlimited window cleared");
                        setTimeout(() => setSuccess(""), 4000);
                      } catch (e) {
                        setError(e.response?.data?.detail || "Failed to clear");
                      } finally {
                        setCaptainUnlimitedSaving(false);
                      }
                    }}
                    disabled={captainUnlimitedSaving || (!captainUnlimitedUntil && !captainUnlimitedInput)}
                    className="px-4 py-2 bg-slate-700 text-slate-200 rounded-lg text-sm font-medium hover:bg-slate-600 transition-colors disabled:opacity-50"
                    data-testid="captain-unlimited-clear-btn"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* Match Day Composition Rules */}
              <MatchDayCompositionRules
                activeTournament={activeTournament}
                getAuthHeader={getAuthHeader}
                setSuccess={setSuccess}
                setError={setError}
              />

              {/* Initialize New Tournament */}
              <div>
                <p className="text-slate-400 text-sm font-medium mb-2">Create New Tournament</p>
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Tournament Name (e.g., APL 2026)"
                    value={newTournamentName}
                    onChange={(e) => setNewTournamentName(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500"
                    data-testid="tournament-name-input"
                  />
                  <input
                    type="text"
                    placeholder="Cricbuzz Series ID (e.g., 9241)"
                    value={newTournamentSeriesId}
                    onChange={(e) => setNewTournamentSeriesId(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500"
                    data-testid="tournament-series-input"
                  />
                  
                  {/* Option 1: Preserve History */}
                  <button
                    onClick={async () => {
                      if (!newTournamentName || !newTournamentSeriesId) {
                        setError("Enter tournament name and series ID");
                        return;
                      }
                      if (!window.confirm(`Create "${newTournamentName}" tournament? User history will be PRESERVED (points, teams stay but won't show for new tournament).`)) return;
                      setInitializingTournament(true);
                      setError("");
                      try {
                        const headers = await getAuthHeader();
                        const res = await axios.post(`${API}/admin/tournament/create`, {
                          name: newTournamentName,
                          series_id: newTournamentSeriesId
                        }, { headers });
                        setSuccess(res.data.message);
                        setNewTournamentName("");
                        setNewTournamentSeriesId("");
                        const tourRes = await axios.get(`${API}/admin/tournament/active`, { headers });
                        setActiveTournament(tourRes.data.active_tournament);
                        setAllTournaments(tourRes.data.all_tournaments);
                        setTimeout(() => setSuccess(""), 5000);
                      } catch (e) {
                        setError(e.response?.data?.detail || "Failed to create tournament");
                      } finally {
                        setInitializingTournament(false);
                      }
                    }}
                    disabled={initializingTournament}
                    className="w-full py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="create-tournament-btn"
                  >
                    {initializingTournament ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
                    Create Tournament (Preserve History)
                  </button>
                  <p className="text-emerald-400/80 text-xs">✓ Keeps old data. Users start fresh for new tournament but T20 WC history preserved in DB.</p>
                  
                  {/* Option 2: Full Reset */}
                  <button
                    onClick={async () => {
                      if (!newTournamentName || !newTournamentSeriesId) {
                        setError("Enter tournament name and series ID");
                        return;
                      }
                      if (!window.confirm(`⚠️ WARNING: This will RESET ALL USER STATS!\n\nAll points, budgets, referral counts will be reset to 0.\nTiers and accounts will be preserved.\n\nContinue?`)) return;
                      setInitializingTournament(true);
                      setError("");
                      try {
                        const headers = await getAuthHeader();
                        const res = await axios.post(`${API}/admin/tournament/init`, {
                          name: newTournamentName,
                          series_id: newTournamentSeriesId
                        }, { headers });
                        setSuccess(res.data.message + ` (${res.data.users_reset} users reset)`);
                        setNewTournamentName("");
                        setNewTournamentSeriesId("");
                        // Refresh tournament data
                        const tourRes = await axios.get(`${API}/admin/tournament/active`, { headers });
                        setActiveTournament(tourRes.data.active_tournament);
                        setAllTournaments(tourRes.data.all_tournaments);
                        setTimeout(() => setSuccess(""), 5000);
                      } catch (e) {
                        setError(e.response?.data?.detail || "Failed to initialize tournament");
                      } finally {
                        setInitializingTournament(false);
                      }
                    }}
                    disabled={initializingTournament}
                    className="w-full py-2 bg-red-500/80 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="init-tournament-btn"
                  >
                    {initializingTournament ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                    Initialize Tournament (Reset All Stats)
                  </button>
                  <p className="text-red-400/80 text-xs">⚠️ Resets ALL user points, budgets, referral counts to 0!</p>
                </div>
              </div>

              {/* Sync Actions */}
              {activeTournament && (
                <div>
                  <p className="text-slate-400 text-sm font-medium mb-2">Sync from Cricbuzz</p>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        setSyncingMatches(true);
                        setError("");
                        try {
                          const headers = await getAuthHeader();
                          const res = await axios.post(`${API}/admin/tournament/sync-from-cricbuzz`, {}, { headers });
                          setSuccess(res.data.message);
                          setTimeout(() => setSuccess(""), 5000);
                        } catch (e) {
                          setError(e.response?.data?.detail || "Failed to sync matches");
                        } finally {
                          setSyncingMatches(false);
                        }
                      }}
                      disabled={syncingMatches}
                      className="flex-1 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="sync-matches-btn"
                    >
                      {syncingMatches ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                      Sync Matches
                    </button>
                    <button
                      onClick={async () => {
                        setSyncingPlayers(true);
                        setError("");
                        try {
                          const headers = await getAuthHeader();
                          const res = await axios.post(`${API}/admin/tournament/sync-players-from-cricbuzz`, {}, { headers });
                          setSuccess(res.data.message);
                          setTimeout(() => setSuccess(""), 5000);
                        } catch (e) {
                          setError(e.response?.data?.detail || "Failed to sync players");
                        } finally {
                          setSyncingPlayers(false);
                        }
                      }}
                      disabled={syncingPlayers}
                      className="flex-1 py-2 bg-cyan-500 text-white rounded-lg text-sm font-medium hover:bg-cyan-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="sync-players-btn"
                    >
                      {syncingPlayers ? <Loader2 className="animate-spin" size={16} /> : <Users size={16} />}
                      Sync Players
                    </button>
                  </div>
                  
                  {/* Reset Players Button */}
                  <button
                    onClick={async () => {
                      setError("");
                      try {
                        const headers = await getAuthHeader();
                        const res = await axios.post(`${API}/admin/tournament/reset-players`, {}, { headers });
                        setSuccess(res.data.message + " - Now click Sync Players to re-import.");
                        setTimeout(() => setSuccess(""), 8000);
                      } catch (e) {
                        setError(e.response?.data?.detail || "Failed to reset players");
                      }
                    }}
                    className="w-full py-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-colors flex items-center justify-center gap-2"
                    data-testid="reset-players-btn"
                  >
                    <Trash2 size={16} />
                    Reset Players (Delete All)
                  </button>
                  
                  {/* Reset All User Stats Button */}
                  <button
                    onClick={async () => {
                      setError("");
                      try {
                        const headers = await getAuthHeader();
                        const res = await axios.post(`${API}/admin/tournament/reset-user-stats`, {}, { headers });
                        setSuccess(res.data.message);
                        setTimeout(() => setSuccess(""), 8000);
                      } catch (e) {
                        setError(e.response?.data?.detail || "Failed to reset user stats");
                      }
                    }}
                    className="w-full py-2 bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-lg text-sm font-medium hover:bg-orange-500/30 transition-colors flex items-center justify-center gap-2"
                    data-testid="reset-user-stats-btn"
                  >
                    <RefreshCw size={16} />
                    Reset All User Stats (Clean Slate)
                  </button>
                </div>
              )}

              {/* All Tournaments List */}
              {allTournaments.length > 0 && (
                <div>
                  <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">All Tournaments</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {allTournaments.map(t => (
                      <div key={t.id} className="flex items-center justify-between bg-slate-900/30 rounded-lg p-2">
                        <div>
                          <p className="text-white text-sm">{t.name}</p>
                          <p className="text-slate-500 text-xs">Series: {t.series_id || t.data_source_series_id || "—"}</p>
                        </div>
                        {activeTournament?.tournament_id === t.id ? (
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">Active</span>
                        ) : (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Switch active tournament to "${t.name}"? No data will be reset. The current tournament's data stays in the DB and reappears if you switch back.`)) return;
                              setError("");
                              try {
                                const headers = await getAuthHeader();
                                const res = await axios.post(`${API}/admin/tournament/activate`, { tournament_id: t.id }, { headers });
                                setSuccess(res.data.message);
                                const tourRes = await axios.get(`${API}/admin/tournament/active`, { headers });
                                setActiveTournament(tourRes.data.active_tournament);
                                setAllTournaments(tourRes.data.all_tournaments);
                                setTimeout(() => setSuccess(""), 6000);
                              } catch (e) {
                                setError(e.response?.data?.detail || "Failed to activate tournament");
                              }
                            }}
                            className="px-2 py-0.5 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded text-xs hover:bg-amber-500/30 transition-colors"
                            data-testid={`activate-tournament-${t.id}`}
                          >
                            Activate
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Archive Old Leagues */}
              <div className="mt-4 pt-4 border-t border-slate-700">
                <p className="text-slate-400 text-sm font-medium mb-2">League Management</p>
                <button
                  onClick={async () => {
                    if (!window.confirm("Archive all T20 WC leagues (leagues without tournament_id)? They will be hidden from users.")) return;
                    try {
                      const headers = await getAuthHeader();
                      const res = await axios.post(`${API}/admin/leagues/archive-t20wc`, {}, { headers });
                      setSuccess(res.data.message);
                      setTimeout(() => setSuccess(""), 5000);
                    } catch (e) {
                      setError(e.response?.data?.detail || "Failed to archive leagues");
                    }
                  }}
                  className="w-full py-2 bg-amber-500/20 text-amber-400 rounded-lg text-sm font-medium hover:bg-amber-500/30 transition-colors flex items-center justify-center gap-2"
                  data-testid="archive-t20wc-leagues-btn"
                >
                  <Archive size={16} /> Archive T20 WC Leagues
                </button>
                <p className="text-slate-500 text-xs mt-1">Hides old leagues without tournament_id from users</p>
              </div>
            </div>
          )}

          {/* Late Joiner Backfill Section */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden mt-4" data-testid="backfill-section">
            <button 
              onClick={() => {
                setShowBackfillSection(!showBackfillSection);
                if (!showBackfillSection) fetchPendingBackfillUsers();
              }} 
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/30 transition-all"
            >
              <div className="flex items-center gap-3">
                <Users className="text-amber-400" size={20} />
                <span className="font-semibold text-white">Late Joiner Backfill</span>
                {backfillStats.pending_count > 0 && (
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs font-medium">
                    {backfillStats.pending_count} pending
                  </span>
                )}
              </div>
              {showBackfillSection ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
            </button>
            
            {showBackfillSection && (
              <div className="p-4 border-t border-slate-700 space-y-4">
                {/* Stats Overview */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
                    <p className="text-amber-400 text-2xl font-bold">{backfillStats.pending_count}</p>
                    <p className="text-slate-400 text-xs">Pending</p>
                  </div>
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-center">
                    <p className="text-emerald-400 text-2xl font-bold">{backfillStats.backfilled_count}</p>
                    <p className="text-slate-400 text-xs">Backfilled</p>
                  </div>
                  <div className="bg-slate-700/30 border border-slate-600 rounded-lg p-3 text-center">
                    <p className="text-white text-2xl font-bold">{backfillStats.total_teams || (backfillStats.pending_count + backfillStats.backfilled_count)}</p>
                    <p className="text-slate-400 text-xs">Total Teams</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={handleBackfillAll}
                    disabled={backfillProcessing || backfillStats.pending_count === 0}
                    className="flex-1 py-2 bg-amber-500 text-black rounded-lg text-sm font-bold hover:bg-amber-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="backfill-all-btn"
                  >
                    {backfillProcessing ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />}
                    Backfill All Pending
                  </button>
                  <button
                    onClick={handleBackfillSelected}
                    disabled={backfillProcessing || selectedBackfillUsers.length === 0}
                    className="py-2 px-4 bg-slate-600 text-white rounded-lg text-sm font-medium hover:bg-slate-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="backfill-selected-btn"
                  >
                    Backfill Selected ({selectedBackfillUsers.length})
                  </button>
                  <button
                    onClick={fetchPendingBackfillUsers}
                    disabled={backfillLoading}
                    className="py-2 px-3 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-all"
                    data-testid="refresh-backfill-btn"
                  >
                    <RefreshCw size={16} className={backfillLoading ? "animate-spin" : ""} />
                  </button>
                </div>

                {/* Backfill Results */}
                {backfillResults && (
                  <div className={`border rounded-lg p-3 ${
                    backfillResults.users_processed === 0 
                      ? 'bg-amber-500/10 border-amber-500/30' 
                      : 'bg-emerald-500/10 border-emerald-500/30'
                  }`}>
                    <p className={`font-medium mb-2 ${
                      backfillResults.users_processed === 0 ? 'text-amber-400' : 'text-emerald-400'
                    }`}>
                      {backfillResults.users_processed === 0 ? 'No Changes Made' : 'Backfill Complete'}
                    </p>
                    <p className="text-slate-300 text-sm">{backfillResults.message}</p>
                    {backfillResults.results && backfillResults.results.length > 0 ? (
                      <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                        {backfillResults.results.map((r, i) => (
                          <div key={i} className="text-xs text-slate-400 flex justify-between">
                            <span>{r.team_name || r.user_id}</span>
                            <span>
                              {r.missed_matches === 0 
                                ? 'No completed matches to backfill'
                                : `+${r.retroactive_points} pts, -${r.penalty_applied} penalty (${r.missed_matches} missed)`
                              }
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : backfillResults.users_processed === 0 && (
                      <p className="text-xs text-amber-400/70 mt-2">
                        Note: Backfill requires completed tournament matches. No APL 2026 matches have been completed yet.
                      </p>
                    )}
                  </div>
                )}

                {/* Pending Users List */}
                {backfillLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="animate-spin text-amber-400" size={24} />
                  </div>
                ) : pendingBackfillUsers.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-slate-400 text-xs uppercase tracking-wider">Pending Backfill Users</p>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {pendingBackfillUsers.map(user => (
                        <div 
                          key={user.user_id} 
                          className={`flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer ${
                            selectedBackfillUsers.includes(user.user_id) 
                              ? 'bg-amber-500/20 border border-amber-500/50' 
                              : 'bg-slate-900/50 border border-slate-700 hover:border-slate-600'
                          }`}
                          onClick={() => toggleBackfillUserSelection(user.user_id)}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={selectedBackfillUsers.includes(user.user_id)}
                              onChange={() => {}}
                              className="w-4 h-4 rounded border-slate-600 text-amber-500 focus:ring-amber-500"
                            />
                            <div>
                              <p className="text-white text-sm font-medium">{user.display_name}</p>
                              <p className="text-slate-500 text-xs">{user.team_name || 'No team name'}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-slate-400 text-xs">
                              Created: {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                            </p>
                            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">
                              {user.backfill_status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <CheckCircle className="mx-auto text-emerald-400 mb-2" size={32} />
                    <p className="text-emerald-400 font-medium">All users backfilled!</p>
                    <p className="text-slate-500 text-sm">No pending users require backfill.</p>
                  </div>
                )}

                {/* Debug Info */}
                {backfillStats._debug && (
                  <div className="bg-slate-900/70 border border-slate-700 rounded-lg p-3 mt-3">
                    <p className="text-xs text-slate-500 font-mono whitespace-pre-wrap">
                      {JSON.stringify(backfillStats._debug, null, 2)}
                    </p>
                    {backfillStats.message && <p className="text-xs text-amber-400 mt-1">{backfillStats.message}</p>}
                  </div>
                )}

                {/* Info Box */}
                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">
                    <strong className="text-amber-400">Late Joiner Penalty:</strong> Users who join after matches have been played 
                    receive retroactive points for their team's performance, but are penalized -25 points per missed match. 
                    This balances the hindsight advantage of picking players after seeing their performance.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Match Sponsors Section */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mt-4">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Gift size={18} className="text-purple-400" />
              Match Sponsors
            </h3>
            <p className="text-slate-400 text-sm mb-3">View and manage sponsors for matches.</p>
            <button
              onClick={async () => {
                try {
                  const headers = await getAuthHeader();
                  const res = await axios.get(`${API}/matches`, { headers });
                  const matchesWithSponsors = (res.data.matches || []).filter(m => m.sponsor);
                  setSponsoredMatches(matchesWithSponsors);
                } catch (e) {
                  setError("Failed to load matches");
                }
              }}
              className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-500/30 transition-all flex items-center gap-2 mb-3"
            >
              <RefreshCw size={16} />
              Load Sponsored Matches
            </button>
            {sponsoredMatches.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {sponsoredMatches.map(m => (
                  <div key={m.match_id} className="bg-slate-900/30 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-white text-sm font-medium">{m.team1_short || m.team1} vs {m.team2_short || m.team2}</p>
                        <p className="text-slate-500 text-xs">{m.date} • Match ID: {m.match_id}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-purple-400 text-sm font-medium">{m.sponsor?.name}</p>
                        <p className="text-slate-500 text-xs truncate max-w-[150px]">{m.sponsor?.link}</p>
                      </div>
                    </div>
                    {m.sponsor?.description && (
                      <p className="text-slate-400 text-xs bg-slate-800/50 rounded p-2 mb-2">{m.sponsor.description}</p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSponsorMatchId(m.match_id);
                          setSponsorName(m.sponsor?.name || "");
                          setSponsorLogoUrl(m.sponsor?.logo_url || "");
                          setSponsorLink(m.sponsor?.link || "");
                          setSponsorDescription(m.sponsor?.description || "");
                        }}
                        className="flex-1 px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs hover:bg-blue-500/30 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Remove sponsor from ${m.team1_short || m.team1} vs ${m.team2_short || m.team2}?`)) return;
                          try {
                            const headers = await getAuthHeader();
                            await axios.delete(`${API}/admin/matches/${m.match_id}/sponsor`, { headers });
                            setSponsoredMatches(prev => prev.filter(match => match.match_id !== m.match_id));
                            setSuccess("Sponsor removed");
                            setTimeout(() => setSuccess(""), 3000);
                          } catch (e) {
                            setError("Failed to remove sponsor");
                          }
                        }}
                        className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs hover:bg-red-500/30 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">Click "Load Sponsored Matches" to see which matches have sponsors.</p>
            )}
          </div>
        </div>
    </div>
  );
}
